import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faCalendarPlus, faArrowLeft, faCheck, faTimes, faCalendarAlt, faMapMarkerAlt, faTicketAlt, faDollarSign, faUsers, faUserCheck, faEye, faChartBar, faChevronDown, faVideo, faToggleOn, faToggleOff, faGift, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import config from '../config/config';
import TimezoneSelector from './TimezoneSelector';
import GoogleMapsLocationPicker from './GoogleMapsLocationPicker';
import VenueManagement from './VenueManagement';
import { formatEventDate, isoToDatetimeLocal, datetimeLocalToISO } from '../utils/dateFormatter';
import virtualEventService from '../services/virtualEventService';
import PostEventContentPanel from './PostEventContentPanel';
import './EventManagementPage.css';

// Local SVG placeholder to avoid external API calls
const DEFAULT_EVENT_IMAGE = "data:image/svg+xml,%3Csvg width='300' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='%23e0e0e0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='16' fill='%235c6bc0'%3EEvent%3C/text%3E%3C/svg%3E";

const EventManagementPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // Tab state for switching between Events and Venues
  const [activeTab, setActiveTab] = useState('events'); // 'events' or 'venues'
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [imageCache, setImageCache] = useState(new Map());
  
  // Teams state
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  
  // Venues state
  const [venues, setVenues] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [useExistingVenue, setUseExistingVenue] = useState(true); // Toggle between existing/new venue (Add Event)
  const [useExistingVenueEdit, setUseExistingVenueEdit] = useState(true); // Toggle for Edit Event
  const [showVenueModal, setShowVenueModal] = useState(false); // Show venue selection modal
  const [venueModalType, setVenueModalType] = useState('new'); // 'new' for Add Event, 'edit' for Edit Event
  const [venueSearchQuery, setVenueSearchQuery] = useState(''); // Search filter in modal
  const [saveNewVenue, setSaveNewVenue] = useState(true); // Save new venue to database for Add Event
  const [saveNewVenueEdit, setSaveNewVenueEdit] = useState(true); // Save new venue to database for Edit Event
  const [customVenueName, setCustomVenueName] = useState(''); // Custom venue name for Add Event
  const [customVenueNameEdit, setCustomVenueNameEdit] = useState(''); // Custom venue name for Edit Event

  // Stagetimer rooms state
  const [stagetimerRooms, setStagetimerRooms] = useState([]);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [newRoomId, setNewRoomId] = useState('');
  const [addingRoom, setAddingRoom] = useState(false);
  const [addRoomError, setAddRoomError] = useState('');
  
  // Ticket management states
  const [showTicketManagement, setShowTicketManagement] = useState(false);
  const [currentEventForTickets, setCurrentEventForTickets] = useState(null);
  const [ticketPrices, setTicketPrices] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  
  // RSVP management states
  const [showRSVPManagement, setShowRSVPManagement] = useState(false);
  const [currentEventForRSVP, setCurrentEventForRSVP] = useState(null);
  const [rsvpData, setRSVPData] = useState([]);
  const [rsvpSummary, setRSVPSummary] = useState(null);
  const [loadingRSVPs, setLoadingRSVPs] = useState(false);
  const [rsvpActiveTab, setRSVPActiveTab] = useState('summary'); // 'summary' or 'details'
  
  // User profile states
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingUserProfile, setLoadingUserProfile] = useState(false);
  
  // Event details modal state
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedEventForDetails, setSelectedEventForDetails] = useState(null);
  const [modalVirtualDetails, setModalVirtualDetails] = useState(null);
  const [modalVirtualDetailsLoading, setModalVirtualDetailsLoading] = useState(false);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Post-event content panel state
  const [showPostEventContent, setShowPostEventContent] = useState(false);
  const [postEventContentEvent, setPostEventContentEvent] = useState(null);

  // Virtual config modal states
  const [showVirtualConfigModal, setShowVirtualConfigModal] = useState(false);
  const [virtualConfigEvent, setVirtualConfigEvent] = useState(null);
  const [virtualConfigData, setVirtualConfigData] = useState(null);
  const [virtualConfigLoading, setVirtualConfigLoading] = useState(false);
  const [virtualConfigSaving, setVirtualConfigSaving] = useState(false);
  const [virtualConfigForm, setVirtualConfigForm] = useState({
    streamingUrl: '',
    chatUrl: '',
    accessWindowHours: 4,
    allowReplay: false,
    replayDurationHours: null,
    isActive: true
  });

  // Track collapsed months
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());

  // DV-262: month-based lazy loading. `monthSummaries` (from /months) drives the
  // collapsed headers + counts; each month's events are loaded on demand into
  // `events` and its key recorded in `loadedMonths`.
  const [monthSummaries, setMonthSummaries] = useState([]); // [{ month: 'YYYY-MM', count }]
  const [loadedMonths, setLoadedMonths] = useState(new Set());
  const [loadingMonths, setLoadingMonths] = useState(new Set());

  // Overflow (⋮) menu shared across event cards
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [cardMenuEvent, setCardMenuEvent] = useState(null);

  const isoMonthKey = (isoDate) => (isoDate ? String(isoDate).slice(0, 7) : '');
  const currentMonthKey = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  };

  // Toggle month collapse/expand; lazy-load the month's events the first time it opens.
  const toggleMonthCollapse = (monthKey) => {
    const willExpand = collapsedMonths.has(monthKey);
    setCollapsedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthKey)) {
        newSet.delete(monthKey);
      } else {
        newSet.add(monthKey);
      }
      return newSet;
    });
    if (willExpand) {
      fetchEventsForMonth(monthKey);
    }
  };

  const openCardMenu = (e, event) => {
    e.stopPropagation();
    setCardMenuAnchor(e.currentTarget);
    setCardMenuEvent(event);
  };
  const closeCardMenu = () => {
    setCardMenuAnchor(null);
    setCardMenuEvent(null);
  };
  const runCardMenuAction = (fn) => {
    const ev = cardMenuEvent;
    closeCardMenu();
    if (ev) fn(ev);
  };
  
  const [ticketFormData, setTicketFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    ticketType: 'REGULAR',
    taxPercentage: 0,
    serviceCharge: 0,
    availableQuantity: 100000,  // Default capacity set to 100000
    active: true,
    saleStartDate: '',
    saleEndDate: '',
    earlyBirdPrice: '',
    earlyBirdEndDate: '',
    groupDiscountPrice: '',
    minimumGroupSize: '',
    displayOrder: 1
  });
  
  // Calendar preview state for recurring events
  const [previewCalendarMonth, setPreviewCalendarMonth] = useState(new Date().getMonth());
  const [previewCalendarYear, setPreviewCalendarYear] = useState(new Date().getFullYear());
  
  // Helper to convert day number (0=Sunday) to day name
  const getDayOfWeekName = useCallback((dayNum) => {
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    return days[dayNum];
  }, []);
  
  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    eventType: 'CONFERENCE',
    capacity: 100000,  // Default capacity set to 100000
    location: '',
    image: null,
    eligibleTeams: [],
    timezone: 'America/Los_Angeles',
    venue: null,  // Will store venue data from LocationPicker
    venueId: null,  // Will store venue ID if venue is selected/saved
    tickets: [],  // Will store ticket price info to create with event
    minPinLevelRank: null,  // Minimum pin level rank for EXCLUSIVE events (0-18)
    isRecurring: false,  // Whether this is a recurring event
    recurrencePattern: {
      frequency: 'WEEKLY',  // 'WEEKLY' or 'MONTHLY'
      interval: 1,  // Every N weeks/months
      daysOfWeek: [],  // For weekly: ['MONDAY', 'TUESDAY', ...]
      daysOfMonth: [],  // For monthly: [1, 15, 30, ...]
      excludeDates: [],  // Dates to exclude (array of date strings)
      maxOccurrences: 10,  // Maximum number of events to create
      endDate: null  // Alternative to maxOccurrences
    },
    isVirtualEvent: false,  // Whether this is a virtual/online event
    stagetimerRoomId: '',
    hotelBookingUrl: '',  // Optional hotel booking link shown to ticket holders
    virtualEventDetails: {
      streamingUrl: '',
      chatUrl: '',
      accessWindowHours: 4,
      allowReplay: false,
      replayDurationHours: null,
      isActive: true
    }
  });

  const [editEvent, setEditEvent] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    eventType: 'CONFERENCE',
    capacity: 100000,
    location: '',
    image: null,
    status: 'UPCOMING',
    eligibleTeams: [],
    timezone: 'America/Los_Angeles',
    venue: null,
    venueId: null,
    minPinLevelRank: null,
    isVirtualEvent: false,
    stagetimerRoomId: '',
    hotelBookingUrl: '',  // Optional hotel booking link shown to ticket holders
    virtualEventDetails: {
      streamingUrl: '',
      chatUrl: '',
      accessWindowHours: 4,
      allowReplay: false,
      replayDurationHours: null,
      isActive: true
    }
  });

  // Generate preview dates for recurring events
  const generatePreviewDates = useCallback(() => {
    if (!newEvent.isRecurring || !newEvent.startDate) return [];
    
    const { frequency, interval, daysOfWeek, daysOfMonth, maxOccurrences, endDate } = newEvent.recurrencePattern;
    
    // Need at least frequency and start date
    if (!frequency) return [];
    
    try {
      // Parse start date (datetime-local format: YYYY-MM-DDTHH:mm)
      const startDateTime = new Date(newEvent.startDate);
      if (isNaN(startDateTime.getTime())) return [];
      
      const dates = [];
      let currentDate = new Date(startDateTime);
      const effectiveMaxOccurrences = maxOccurrences || 100; // Default max to prevent infinite loop
      const effectiveInterval = interval || 1;
      
      // Parse end date if provided
      let endDateTime = null;
      if (endDate) {
        endDateTime = new Date(endDate);
        if (isNaN(endDateTime.getTime())) endDateTime = null;
      }
      
      // Weekly recurrence
      if (frequency === 'WEEKLY') {
        // If no specific days selected, use start date's day of week
        const targetDays = daysOfWeek.length > 0 ? daysOfWeek : [getDayOfWeekName(startDateTime.getDay())];
        
        let weekCount = 0;
        const maxWeeks = effectiveMaxOccurrences * effectiveInterval * 2; // Safety limit
        
        while (dates.length < effectiveMaxOccurrences && weekCount < maxWeeks) {
          // Check if we've passed the end date
          if (endDateTime && currentDate > endDateTime) break;
          
          // Check current week for matching days
          const weekStart = new Date(currentDate);
          weekStart.setDate(weekStart.getDate() - (weekStart.getDay() === 0 ? 6 : weekStart.getDay() - 1)); // Start from Monday
          
          for (let i = 0; i < 7; i++) {
            const checkDate = new Date(weekStart);
            checkDate.setDate(weekStart.getDate() + i);
            checkDate.setHours(startDateTime.getHours(), startDateTime.getMinutes(), 0, 0);
            
            // Only consider dates on or after start date
            if (checkDate < startDateTime) continue;
            
            const dayName = getDayOfWeekName(checkDate.getDay());
            if (targetDays.includes(dayName)) {
              if (endDateTime && checkDate > endDateTime) break;
              if (dates.length < effectiveMaxOccurrences) {
                dates.push(new Date(checkDate));
              }
            }
          }
          
          // Move to next interval
          currentDate.setDate(currentDate.getDate() + (7 * effectiveInterval));
          weekCount++;
        }
      }
      // Monthly recurrence
      else if (frequency === 'MONTHLY') {
        // If no specific days selected, use start date's day of month
        const targetDays = daysOfMonth.length > 0 ? daysOfMonth : [startDateTime.getDate()];
        
        let monthCount = 0;
        const maxMonths = effectiveMaxOccurrences * effectiveInterval * 2; // Safety limit
        
        while (dates.length < effectiveMaxOccurrences && monthCount < maxMonths) {
          // Check if we've passed the end date
          if (endDateTime && currentDate > endDateTime) break;
          
          // Check current month for matching days
          for (const day of targetDays) {
            const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            checkDate.setHours(startDateTime.getHours(), startDateTime.getMinutes(), 0, 0);
            
            // Only consider valid dates (day exists in month) and on or after start date
            if (checkDate.getMonth() !== currentDate.getMonth()) continue;
            if (checkDate < startDateTime) continue;
            
            if (endDateTime && checkDate > endDateTime) break;
            if (dates.length < effectiveMaxOccurrences) {
              dates.push(new Date(checkDate));
            }
          }
          
          // Move to next interval
          currentDate.setMonth(currentDate.getMonth() + effectiveInterval);
          monthCount++;
        }
      }
      
      // Sort and return unique dates
      return dates
        .sort((a, b) => a - b)
        .filter((date, index, self) => 
          index === 0 || date.getTime() !== self[index - 1].getTime()
        );
        
    } catch (error) {
      console.error('Error generating preview dates:', error);
      return [];
    }
  }, [newEvent.isRecurring, newEvent.startDate, newEvent.recurrencePattern, getDayOfWeekName]);
  
  // Memoize preview dates
  const previewDates = useMemo(() => generatePreviewDates(), [generatePreviewDates]);
  
  // Check if preview should be shown
  const shouldShowPreview = useMemo(() => {
    if (!newEvent.isRecurring || !newEvent.startDate) return false;
    const { frequency, daysOfWeek, daysOfMonth, maxOccurrences, endDate } = newEvent.recurrencePattern;
    
    // Show if we have frequency and start date
    return frequency && newEvent.startDate && (maxOccurrences > 0 || endDate || daysOfWeek.length > 0 || daysOfMonth.length > 0);
  }, [newEvent.isRecurring, newEvent.startDate, newEvent.recurrencePattern]);
  
  // Generate calendar grid for current preview month
  const getCalendarGrid = useMemo(() => {
    const firstDay = new Date(previewCalendarYear, previewCalendarMonth, 1);
    const lastDay = new Date(previewCalendarYear, previewCalendarMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    // Adjust to start from Monday (1) instead of Sunday (0)
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    // Create array of dates for the month
    const grid = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < adjustedStartDay; i++) {
      grid.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(previewCalendarYear, previewCalendarMonth, day);
      grid.push(date);
    }
    
    return grid;
  }, [previewCalendarMonth, previewCalendarYear]);
  
  // Check if a date is in preview dates
  const isDateInPreview = useCallback((date) => {
    if (!date) return false;
    return previewDates.some(previewDate => 
      previewDate.getDate() === date.getDate() &&
      previewDate.getMonth() === date.getMonth() &&
      previewDate.getFullYear() === date.getFullYear()
    );
  }, [previewDates]);
  
  // Check if a date is today
  const isToday = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }, []);
  
  // Navigate calendar months
  const handlePrevMonth = useCallback(() => {
    if (previewCalendarMonth === 0) {
      setPreviewCalendarMonth(11);
      setPreviewCalendarYear(previewCalendarYear - 1);
    } else {
      setPreviewCalendarMonth(previewCalendarMonth - 1);
    }
  }, [previewCalendarMonth, previewCalendarYear]);
  
  const handleNextMonth = useCallback(() => {
    if (previewCalendarMonth === 11) {
      setPreviewCalendarMonth(0);
      setPreviewCalendarYear(previewCalendarYear + 1);
    } else {
      setPreviewCalendarMonth(previewCalendarMonth + 1);
    }
  }, [previewCalendarMonth, previewCalendarYear]);
  
  // Format month name
  const getMonthName = useCallback((month, year) => {
    return new Date(year, month, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }, []);
  
  // Initialize calendar to start date's month when recurring is enabled
  useEffect(() => {
    if (newEvent.isRecurring && newEvent.startDate) {
      const startDate = new Date(newEvent.startDate);
      if (!isNaN(startDate.getTime())) {
        setPreviewCalendarMonth(startDate.getMonth());
        setPreviewCalendarYear(startDate.getFullYear());
      }
    }
  }, [newEvent.isRecurring, newEvent.startDate]);

  // Load month summaries + current month, teams, and venues
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      const months = await fetchEventMonths();
      const ck = currentMonthKey();
      // Every month except the current one starts collapsed.
      setCollapsedMonths(new Set(months.map(m => m.month).filter(k => k !== ck)));
      // Auto-load the current month so it renders expanded on open.
      await fetchEventsForMonth(ck, { force: true });
      setLoading(false);
    };
    init();
    fetchTeams();
    fetchVenues();
    fetchStagetimerRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessToken]);

  // DV-262: lightweight per-month counts (drives the collapsed month headers).
  const fetchEventMonths = async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/months`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch event months');
      const data = await response.json();
      const list = Array.isArray(data.data) ? data.data : [];
      setMonthSummaries(list);
      return list;
    } catch (err) {
      console.error('Error fetching event months:', err);
      setError(err.message);
      setMonthSummaries([]);
      return [];
    }
  };

  // DV-262: load one month's events on demand and merge them into `events`.
  const fetchEventsForMonth = async (monthKey, { force = false } = {}) => {
    if (!monthKey) return;
    if (!force && (loadedMonths.has(monthKey) || loadingMonths.has(monthKey))) return;
    setLoadingMonths(prev => new Set(prev).add(monthKey));
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/by-month?month=${monthKey}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch events for month');
      const data = await response.json();
      const list = Array.isArray(data.data) ? data.data : [];
      setEvents(prev => {
        const others = prev.filter(e => isoMonthKey(e.startDate) !== monthKey);
        return [...others, ...list];
      });
      setLoadedMonths(prev => new Set(prev).add(monthKey));
    } catch (err) {
      console.error(`Error fetching events for ${monthKey}:`, err);
      setError(err.message);
    } finally {
      setLoadingMonths(prev => {
        const next = new Set(prev);
        next.delete(monthKey);
        return next;
      });
    }
  };

  // Refresh month counts and re-load the currently-loaded months (after a mutation).
  const refreshLoadedData = async () => {
    await fetchEventMonths();
    const months = [...loadedMonths];
    await Promise.all(months.map(k => fetchEventsForMonth(k, { force: true })));
  };

  // Fetch teams function
  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch teams');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setTeams(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load teams');
      }

    } catch (err) {
      console.error('Error fetching teams:', err);
      // Don't show error for teams fetch as it's not critical
    } finally {
      setLoadingTeams(false);
    }
  };

  // Fetch venues function
  const fetchVenues = async () => {
    try {
      setLoadingVenues(true);
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/venues`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch venues');
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setVenues(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load venues');
      }

    } catch (err) {
      console.error('Error fetching venues:', err);
      // Don't show error for venues fetch as it's not critical
    } finally {
      setLoadingVenues(false);
    }
  };

  const fetchStagetimerRooms = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/stagetimer/rooms`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setStagetimerRooms(data.data || []);
        }
      }
    } catch (err) {
      console.error('Error fetching stagetimer rooms:', err);
    }
  };

  const handleRegisterRoom = async () => {
    if (!newRoomId.trim()) return;
    setAddingRoom(true);
    setAddRoomError('');
    try {
      const token = await getAccessToken();
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/stagetimer/rooms`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId: newRoomId.trim() })
      });
      const data = await response.json();
      if (response.ok && data.status === 'success') {
        await fetchStagetimerRooms();
        setNewRoomId('');
        setShowAddRoomModal(false);
      } else {
        setAddRoomError(data.message || 'Failed to register room');
      }
    } catch (err) {
      setAddRoomError('Failed to register room: ' + err.message);
    } finally {
      setAddingRoom(false);
    }
  };

  // Image caching and optimization functions
  const updateImageCache = useCallback((imageUrl, isValid) => {
    if (imageUrl && imageUrl !== DEFAULT_EVENT_IMAGE) {
      setImageCache(prev => new Map(prev).set(imageUrl, isValid));
    }
  }, []);

  const getOptimizedImageUrl = useCallback((originalUrl) => {
    if (!originalUrl) return DEFAULT_EVENT_IMAGE;
    
    // Check if we've already verified this image doesn't work
    if (imageCache.has(originalUrl) && !imageCache.get(originalUrl)) {
      return DEFAULT_EVENT_IMAGE;
    }
    
    return originalUrl;
  }, [imageCache]);

  const handleImageError = useCallback((e, eventId) => {
    const originalUrl = e.target.getAttribute('data-original-src');
    if (originalUrl && originalUrl !== DEFAULT_EVENT_IMAGE) {
      // Mark this image as invalid in cache
      updateImageCache(originalUrl, false);
      // Set fallback image
      e.target.src = DEFAULT_EVENT_IMAGE;
    }
  }, [updateImageCache]);

  const handleImageLoad = useCallback((e) => {
    const originalUrl = e.target.getAttribute('data-original-src');
    if (originalUrl && originalUrl !== DEFAULT_EVENT_IMAGE) {
      updateImageCache(originalUrl, true);
    }
  }, [updateImageCache]);

  // Preload images for better UX
  useEffect(() => {
    events.forEach(event => {
      if (event.imageUrl && event.imageUrl !== DEFAULT_EVENT_IMAGE && !imageCache.has(event.imageUrl)) {
        const img = new Image();
        img.onload = () => updateImageCache(event.imageUrl, true);
        img.onerror = () => updateImageCache(event.imageUrl, false);
        img.src = event.imageUrl;
      }
    });
  }, [events, imageCache, updateImageCache]);

  // (Collapsed months are initialized at mount from the month summaries — see the
  //  init effect above — using ISO 'YYYY-MM' keys.)

  const handleBackToDashboard = () => {
    navigate('/');
  };

  const handleAddEvent = () => {
    setShowAddForm(true);
  };

  // Open venue selection modal
  const handleOpenVenueModal = (type) => {
    setVenueModalType(type);
    setShowVenueModal(true);
    setVenueSearchQuery('');
  };

  // Handle venue selection from modal
  const handleSelectVenueFromModal = (selectedVenue) => {
    // Convert VenueDTO to the format expected by LocationPicker
    const venueData = {
      name: selectedVenue.name,
      formattedAddress: selectedVenue.formattedAddress,
      address: selectedVenue.address,
      city: selectedVenue.city,
      state: selectedVenue.state,
      country: selectedVenue.country,
      countryCode: selectedVenue.countryCode,
      postalCode: selectedVenue.postalCode,
      latitude: selectedVenue.latitude,
      longitude: selectedVenue.longitude,
      timezone: selectedVenue.timezone,
      placeId: selectedVenue.placeId,
      types: selectedVenue.types ? selectedVenue.types.split(',') : [],
      phoneNumber: selectedVenue.phoneNumber,
      website: selectedVenue.website,
      url: selectedVenue.googleMapsUrl,
      rating: selectedVenue.rating,
      userRatingsTotal: selectedVenue.userRatingsTotal
    };
    
    if (venueModalType === 'new') {
      // Add Event
      setNewEvent(prev => ({
        ...prev,
        venue: venueData,
        location: venueData.formattedAddress,
        timezone: venueData.timezone,
        venueId: selectedVenue.id  // Store the venue ID from existing venue
      }));
      // Set custom venue name to the venue's saved name
      setCustomVenueName(selectedVenue.name);
    } else {
      // Edit Event
      setEditEvent(prev => ({
        ...prev,
        venue: venueData,
        location: venueData.formattedAddress,
        timezone: venueData.timezone,
        venueId: selectedVenue.id  // Store the venue ID from existing venue
      }));
      // Set custom venue name to the venue's saved name
      setCustomVenueNameEdit(selectedVenue.name);
    }
    
    // Close modal
    setShowVenueModal(false);
  };

  // Handle venue selection from LocationPicker (Add Event)
  const handleLocationSelect = (venueData) => {
    if (venueData) {
      setNewEvent(prev => ({
        ...prev,
        venue: venueData,
        location: venueData.formattedAddress,
        timezone: venueData.timezone  // Auto-populate timezone!
      }));
      // Set custom venue name to Google Maps name initially
      setCustomVenueName(venueData.name);
      // Default to saving new venues
      setSaveNewVenue(true);
    } else {
      // Venue cleared
      setNewEvent(prev => ({
        ...prev,
        venue: null,
        location: '',
        timezone: 'America/Los_Angeles'  // Reset to default
      }));
      setCustomVenueName('');
      setSaveNewVenue(false);
    }
  };

  // Handle existing venue selection from dropdown (Edit Event)
  const handleEditExistingVenueSelect = (e) => {
    const venueId = e.target.value;
    
    if (!venueId || venueId === '') {
      // Clear venue selection
      setEditEvent(prev => ({
        ...prev,
        venue: null,
        venueId: null
        // Keep existing location and timezone
      }));
      return;
    }
    
    // Find the selected venue
    const selectedVenue = venues.find(v => v.id === parseInt(venueId));
    
    if (selectedVenue) {
      // Convert VenueDTO to the format expected by LocationPicker
      const venueData = {
        name: selectedVenue.name,
        formattedAddress: selectedVenue.formattedAddress,
        address: selectedVenue.address,
        city: selectedVenue.city,
        state: selectedVenue.state,
        country: selectedVenue.country,
        countryCode: selectedVenue.countryCode,
        postalCode: selectedVenue.postalCode,
        latitude: selectedVenue.latitude,
        longitude: selectedVenue.longitude,
        timezone: selectedVenue.timezone,
        placeId: selectedVenue.placeId,
        types: selectedVenue.types ? selectedVenue.types.split(',') : [],
        phoneNumber: selectedVenue.phoneNumber,
        website: selectedVenue.website,
        url: selectedVenue.googleMapsUrl,
        rating: selectedVenue.rating,
        userRatingsTotal: selectedVenue.userRatingsTotal
      };
      
      setEditEvent(prev => ({
        ...prev,
        venue: venueData,
        location: venueData.formattedAddress,
        timezone: venueData.timezone,
        venueId: selectedVenue.id
      }));
    }
  };

  // Handle venue selection from LocationPicker (Edit Event)
  const handleEditLocationSelect = (venueData) => {
    if (venueData) {
      setEditEvent(prev => ({
        ...prev,
        venue: venueData,
        location: venueData.formattedAddress,
        timezone: venueData.timezone  // Auto-populate timezone!
      }));
      // Set custom venue name to Google Maps name initially
      setCustomVenueNameEdit(venueData.name);
      // Default to saving new venues
      setSaveNewVenueEdit(true);
    } else {
      // Venue cleared
      setEditEvent(prev => ({
        ...prev,
        venue: null
        // Keep existing location and timezone
      }));
      setCustomVenueNameEdit('');
      setSaveNewVenueEdit(false);
    }
  };
  
  // Edit event - prepare form with current event data
  const handleEditEvent = async (event) => {
    const eventTimezone = event.timezone || 'America/Los_Angeles';
    
    // If event has venue details, convert to the format expected by LocationPicker
    let venueData = null;
    let venueId = null;
    
    if (event.venue) {
      venueData = {
        name: event.venue.name,
        formattedAddress: event.venue.formattedAddress,
        address: event.venue.address,
        city: event.venue.city,
        state: event.venue.state,
        country: event.venue.country,
        countryCode: event.venue.countryCode,
        postalCode: event.venue.postalCode,
        latitude: event.venue.latitude,
        longitude: event.venue.longitude,
        timezone: event.venue.timezone,
        placeId: event.venue.placeId,
        types: event.venue.types ? event.venue.types.split(',') : [],
        phoneNumber: event.venue.phoneNumber,
        website: event.venue.website,
        url: event.venue.googleMapsUrl,
        rating: event.venue.rating,
        userRatingsTotal: event.venue.userRatingsTotal
      };
      venueId = event.venue.id;
      // Set the custom venue name for edit form
      setCustomVenueNameEdit(event.venue.name);
    } else {
      // Clear custom venue name if no venue
      setCustomVenueNameEdit('');
    }
    
    // Fetch virtual event details if this is a virtual event
    let virtualEventDetails = null;
    if (event.isVirtualEvent) {
      try {
        const response = await virtualEventService.getVirtualEvent(event.id);
        const details = response.data || response;
        virtualEventDetails = {
          streamingUrl: details.streamingUrl || '',
          chatUrl: details.chatUrl || '',
          accessWindowHours: details.accessWindowHours || 4,
          allowReplay: details.allowReplay || false,
          replayDurationHours: details.replayDurationHours || 24,
          isActive: details.isActive !== false
        };
      } catch (err) {
        console.error('Error loading virtual event details:', err);
      }
    }
    
    setEditEvent({
      name: event.name,
      description: event.description || '',
      startDate: isoToDatetimeLocal(event.startDate, eventTimezone),
      endDate: isoToDatetimeLocal(event.endDate, eventTimezone),
      eventType: event.eventType,
      capacity: event.capacity || 100000,  // Default to 100000 if not set
      location: event.isVirtualEvent ? 'Virtual' : (event.location || ''),
      image: null,
      status: event.status,
      eligibleTeams: event.eligibleTeams || [],
      timezone: eventTimezone,
      venue: event.isVirtualEvent ? null : venueData,  // No venue for virtual events
      venueId: event.isVirtualEvent ? null : venueId,  // No venue ID for virtual events
      minPinLevelRank: event.minPinLevelRank || null,
      isVirtualEvent: event.isVirtualEvent || false,
      stagetimerRoomId: event.stagetimerRoomId || '',
      hotelBookingUrl: event.hotelBookingUrl || '',
      virtualEventDetails: virtualEventDetails || {
        streamingUrl: '',
        chatUrl: '',
        accessWindowHours: 4,
        allowReplay: false,
        replayDurationHours: null,
        isActive: true
      }
    });
    
    setCurrentEvent(event);
    setShowEditForm(true);
  };
  
  // Delete event - show confirmation dialog
  const handleDeletePrompt = (event) => {
    setCurrentEvent(event);
    setShowDeleteConfirm(true);
  };

  const handleInputChange = (e, formType = 'new') => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (formType === 'edit') {
        setEditEvent(prev => ({
          ...prev,
          [name]: file
        }));
      } else {
        setNewEvent(prev => ({
          ...prev,
          [name]: file
        }));
      }
    } else {
      if (formType === 'edit') {
        setEditEvent(prev => {
          const updates = { [name]: value };
          
          // Auto-populate end date when start date is selected
          if (name === 'startDate' && value && !prev.endDate) {
            updates.endDate = value;
          }
          
          // Clear minPinLevelRank when event type changes from EXCLUSIVE to another type
          if (name === 'eventType' && value !== 'EXCLUSIVE' && prev.minPinLevelRank) {
            updates.minPinLevelRank = null;
          }
          
          return { ...prev, ...updates };
        });
      } else {
        setNewEvent(prev => {
          const updates = { [name]: value };
          
          // Auto-populate end date when start date is selected
          if (name === 'startDate' && value && !prev.endDate) {
            updates.endDate = value;
          }
          
          // Clear minPinLevelRank when event type changes from EXCLUSIVE to another type
          if (name === 'eventType' && value !== 'EXCLUSIVE' && prev.minPinLevelRank) {
            updates.minPinLevelRank = null;
          }
          
          return { ...prev, ...updates };
        });
      }
    }
  };

  // Handle team selection
  const handleTeamSelection = (e, formType = 'new') => {
    const { value, checked } = e.target;
    
    const updateTeams = (currentTeams) => {
      if (value === 'all') {
        // If "all" is selected, clear other selections and add "all"
        return checked ? ['all'] : [];
      } else {
        // Remove "all" if present and handle individual team selection
        const filteredTeams = currentTeams.filter(team => team !== 'all');
        if (checked) {
          return [...filteredTeams, value];
        } else {
          return filteredTeams.filter(team => team !== value);
        }
      }
    };

    if (formType === 'edit') {
      setEditEvent(prev => ({
        ...prev,
        eligibleTeams: updateTeams(prev.eligibleTeams)
      }));
    } else {
      setNewEvent(prev => ({
        ...prev,
        eligibleTeams: updateTeams(prev.eligibleTeams)
      }));
    }
  };

  // Ticket management for event creation form
  const handleAddTicketToEvent = () => {
    // Calculate default sale dates
    // Sale start: current time in event's timezone
    const now = new Date();
    const nowUTC = now.toISOString();
    const saleStartDefault = isoToDatetimeLocal(nowUTC, newEvent.timezone || 'America/Los_Angeles');
    
    // Sale end: event's end date (already in datetime-local format)
    const saleEndDefault = newEvent.endDate || '';
    
    const newTicket = {
      id: Date.now(), // Temporary ID for frontend
      name: '',
      description: '',
      price: '',
      currency: 'USD',
      ticketType: 'REGULAR',
      taxPercentage: 0,
      serviceCharge: 0,
      availableQuantity: 100000,  // Default to 100000
      active: true,
      saleStartDate: saleStartDefault,
      saleEndDate: saleEndDefault,
      earlyBirdPrice: '',
      earlyBirdEndDate: '',
      groupDiscountPrice: '',
      minimumGroupSize: '',
      displayOrder: newEvent.tickets.length + 1
    };
    
    setNewEvent(prev => ({
      ...prev,
      tickets: [...prev.tickets, newTicket]
    }));
  };
  
  const handleRemoveTicketFromEvent = (ticketId) => {
    setNewEvent(prev => ({
      ...prev,
      tickets: prev.tickets.filter(t => t.id !== ticketId)
    }));
  };
  
  const handleUpdateTicketInEvent = (ticketId, field, value) => {
    setNewEvent(prev => ({
      ...prev,
      tickets: prev.tickets.map(t => 
        t.id === ticketId ? { ...t, [field]: value } : t
      )
    }));
  };

  /**
   * Save a new venue to the database
   */
  const saveVenueToDatabase = async (venueData, token) => {
    try {
      console.log('Saving venue to database:', venueData);
      
      const venuePayload = {
        name: venueData.name,
        formattedAddress: venueData.formattedAddress,
        address: venueData.address || '',
        city: venueData.city || '',
        state: venueData.state || '',
        country: venueData.country || '',
        countryCode: venueData.countryCode || '',
        postalCode: venueData.postalCode || '',
        latitude: venueData.latitude,
        longitude: venueData.longitude,
        timezone: venueData.timezone,
        placeId: venueData.placeId || '',
        types: Array.isArray(venueData.types) ? venueData.types.join(',') : (venueData.types || ''),
        phoneNumber: venueData.phoneNumber || null,
        website: venueData.website || null,
        googleMapsUrl: venueData.url || venueData.googleMapsUrl || null,
        rating: venueData.rating || null,
        userRatingsTotal: venueData.userRatingsTotal || null,
        notes: '',
        active: true
      };

      const response = await fetch(`${config.API_BASE_URL}/em/v1/venues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(venuePayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save venue');
      }

      const data = await response.json();
      console.log('Venue saved successfully:', data.data);
      
      // Refresh venues list
      await fetchVenues();
      
      return data.data;
    } catch (err) {
      console.error('Error saving venue:', err);
      throw err;
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    try {
      setActionLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Save venue to database if checkbox is checked and we're using a new venue
      let savedVenueId = newEvent.venueId; // Use existing venueId if already set
      
      if (!useExistingVenue && newEvent.venue && saveNewVenue) {
        try {
          // Update venue name with custom name if provided
          const venueToSave = {
            ...newEvent.venue,
            name: customVenueName || newEvent.venue.name
          };
          const savedVenue = await saveVenueToDatabase(venueToSave, token);
          savedVenueId = savedVenue.id;  // Capture the saved venue ID
          console.log('Venue saved with ID:', savedVenueId);
          setSuccessMessage('Venue saved successfully! Creating event...');
        } catch (venueErr) {
          // Log the error but continue with event creation
          console.warn('Failed to save venue, continuing with event creation:', venueErr);
        }
      }
      
      // Prepare request data - convert datetime-local to ISO UTC
      const requestData = {
        name: newEvent.name,
        description: newEvent.description,
        startDate: datetimeLocalToISO(newEvent.startDate, newEvent.timezone),
        endDate: datetimeLocalToISO(newEvent.endDate, newEvent.timezone),
        eventType: newEvent.eventType,
        capacity: parseInt(newEvent.capacity),
        location: newEvent.location,
        timezone: newEvent.timezone,
        eligibleTeams: newEvent.eligibleTeams || []
      };
      
      // Include venueId if a venue is associated (either existing or newly saved)
      if (savedVenueId) {
        requestData.venueId = savedVenueId;
        console.log('Including venueId in event creation:', savedVenueId);
      }
      
      // Include minPinLevelRank if event type is EXCLUSIVE
      if (newEvent.eventType === 'EXCLUSIVE' && newEvent.minPinLevelRank) {
        requestData.minPinLevelRank = parseInt(newEvent.minPinLevelRank);
        console.log('Including minPinLevelRank for EXCLUSIVE event:', requestData.minPinLevelRank);
      }
      
      // Include isVirtualEvent flag
      requestData.isVirtualEvent = newEvent.isVirtualEvent || false;

      if (newEvent.stagetimerRoomId) {
        requestData.stagetimerRoomId = newEvent.stagetimerRoomId;
      }

      if (newEvent.hotelBookingUrl && newEvent.hotelBookingUrl.trim()) {
        requestData.hotelBookingUrl = newEvent.hotelBookingUrl.trim();
      }

      // Include recurrence pattern if this is a recurring event
      if (newEvent.isRecurring) {
        requestData.recurrencePattern = {
          frequency: newEvent.recurrencePattern.frequency,
          interval: newEvent.recurrencePattern.interval,
          daysOfWeek: newEvent.recurrencePattern.daysOfWeek.length > 0 ? newEvent.recurrencePattern.daysOfWeek : null,
          daysOfMonth: newEvent.recurrencePattern.daysOfMonth.length > 0 ? newEvent.recurrencePattern.daysOfMonth : null,
          excludeDates: newEvent.recurrencePattern.excludeDates.length > 0 ? newEvent.recurrencePattern.excludeDates : null,
          maxOccurrences: newEvent.recurrencePattern.maxOccurrences,
          endDate: newEvent.recurrencePattern.endDate || null
        };
        console.log('Creating recurring events with pattern:', requestData.recurrencePattern);
        
        // Include ticket price infos if any are defined for recurring events
        if (newEvent.tickets && newEvent.tickets.length > 0) {
          requestData.ticketPriceInfos = newEvent.tickets.map(ticket => ({
            name: ticket.name,
            description: ticket.description,
            price: parseFloat(ticket.price),
            currency: ticket.currency,
            ticketType: ticket.ticketType,
            taxPercentage: parseFloat(ticket.taxPercentage) || 0,
            serviceCharge: parseFloat(ticket.serviceCharge) || 0,
            availableQuantity: parseInt(ticket.availableQuantity),
            active: ticket.active !== false,
            displayOrder: ticket.displayOrder,
            saleStartDate: ticket.saleStartDate ? datetimeLocalToISO(ticket.saleStartDate, newEvent.timezone) : null,
            saleEndDate: ticket.saleEndDate ? datetimeLocalToISO(ticket.saleEndDate, newEvent.timezone) : null,
            earlyBirdPrice: ticket.earlyBirdPrice ? parseFloat(ticket.earlyBirdPrice) : null,
            earlyBirdEndDate: ticket.earlyBirdEndDate ? datetimeLocalToISO(ticket.earlyBirdEndDate, newEvent.timezone) : null,
            groupDiscountPrice: ticket.groupDiscountPrice ? parseFloat(ticket.groupDiscountPrice) : null,
            minimumGroupSize: ticket.minimumGroupSize ? parseInt(ticket.minimumGroupSize) : null
          }));
          console.log(`Including ${requestData.ticketPriceInfos.length} ticket types for recurring events`);
        }
        
        // Include virtual event details if this is a virtual recurring event
        if (newEvent.isVirtualEvent && newEvent.virtualEventDetails.streamingUrl) {
          requestData.virtualEventDetails = {
            streamingUrl: newEvent.virtualEventDetails.streamingUrl,
            chatUrl: newEvent.virtualEventDetails.chatUrl || null,
            accessWindowHours: newEvent.virtualEventDetails.accessWindowHours,
            allowReplay: newEvent.virtualEventDetails.allowReplay,
            replayDurationHours: newEvent.virtualEventDetails.replayDurationHours,
            isActive: true
          };
          console.log('Including virtual event details for recurring events');
        }
      }
      
      let requestOptions;
      
      // Create FormData with JSON as Blob (as backend expects)
      const formData = new FormData();
      
      // Create JSON Blob with correct content type
      const jsonBlob = new Blob([JSON.stringify(requestData)], {
        type: 'application/json'
      });
      
      // Append JSON as Blob with application/json content type
      formData.append('request', jsonBlob);
      
      // Add image if selected
      if (newEvent.image) {
        formData.append('image', newEvent.image);
      }
      
      requestOptions = {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Do NOT set Content-Type header - let browser set it automatically for FormData
        },
        body: formData
      };
      
      // Debug: Log FormData contents
      console.log('Creating event with FormData (JSON as Blob):', {
        hasImage: !!newEvent.image,
        requestDataString: JSON.stringify(requestData),
        jsonBlobType: jsonBlob.type,
        jsonBlobSize: jsonBlob.size,
        formDataEntries: Array.from(formData.entries()).map(([key, value]) => [
          key, 
          value instanceof File ? `File: ${value.name} (${value.type})` : 
          value instanceof Blob ? `Blob: ${value.type} (${value.size} bytes)` : value
        ])
      });
      
      // Additional debug: Log the raw FormData
      console.log('Raw FormData entries:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`${key}:`, `File(${value.name}, ${value.type}, ${value.size} bytes)`);
        } else if (value instanceof Blob) {
          console.log(`${key}:`, `Blob(${value.type}, ${value.size} bytes)`);
          // Try to read the blob content for debugging
          value.text().then(text => console.log(`${key} content:`, text));
        } else {
          console.log(`${key}:`, value);
        }
      }
      
      // Determine API endpoint based on whether this is a recurring event
      const apiEndpoint = newEvent.isRecurring 
        ? `${config.API_BASE_URL}/em/v1/admin/events/recurring`
        : `${config.API_BASE_URL}/em/v1/admin/events`;
      
      // Debug: Try to understand what's being sent
      console.log('Request will be sent to:', apiEndpoint);
      console.log('With authorization header:', `Bearer ${token.substring(0, 20)}...`);
      
      // Debug: Check if FormData is properly constructed
      console.log('FormData constructor:', formData.constructor.name);
      console.log('FormData has entries:', formData.has('request'));
      console.log('FormData has image:', formData.has('image'));
      
      // Debug: Log request details
      console.log('Request details:', {
        url: apiEndpoint,
        method: requestOptions.method,
        headers: requestOptions.headers,
        bodyType: requestOptions.body.constructor.name
      });
      
      // Try multiple approaches to see which one works
      console.log('=== ATTEMPTING MULTIPLE APPROACHES ===');
      
      // Approach 1: Current Blob approach
      console.log('Trying Approach 1: JSON as Blob');
      let response = await fetch(apiEndpoint, requestOptions);
      
      if (!response.ok && response.status === 415) {
        console.log('Approach 1 failed with 415, trying Approach 2...');
        
        // Approach 2: Try with explicit filename for the JSON blob
        const formData2 = new FormData();
        const jsonBlob2 = new Blob([JSON.stringify(requestData)], {
          type: 'application/json'
        });
        formData2.append('request', jsonBlob2, 'request.json'); // Add filename
        
        if (newEvent.image) {
          formData2.append('image', newEvent.image);
        }
        
        const requestOptions2 = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData2
        };
        
        console.log('Trying Approach 2: JSON Blob with filename');
        response = await fetch(apiEndpoint, requestOptions2);
      }
      
      if (!response.ok && response.status === 415) {
        console.log('Approach 2 failed with 415, trying Approach 3...');
        
        // Approach 3: Try sending individual fields
        const formData3 = new FormData();
        formData3.append('name', requestData.name);
        formData3.append('description', requestData.description);
        formData3.append('startDate', requestData.startDate);
        formData3.append('endDate', requestData.endDate);
        formData3.append('eventType', requestData.eventType);
        formData3.append('capacity', requestData.capacity.toString());
        formData3.append('location', requestData.location);
        formData3.append('eligibleTeams', JSON.stringify(requestData.eligibleTeams));
        
        if (newEvent.image) {
          formData3.append('image', newEvent.image);
        }
        
        const requestOptions3 = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData3
        };
        
        console.log('Trying Approach 3: Individual fields');
        response = await fetch(apiEndpoint, requestOptions3);
      }
      
      if (!response.ok && response.status === 415) {
        console.log('Approach 3 failed with 415, trying Approach 4...');
        
        // Approach 4: Try to exactly mimic the cURL command structure
        const formData4 = new FormData();
        
        // Create a File object from the JSON string to mimic cURL behavior
        const jsonFile = new File([JSON.stringify(requestData)], 'request.json', {
          type: 'application/json'
        });
        formData4.append('request', jsonFile);
        
        if (newEvent.image) {
          formData4.append('image', newEvent.image);
        }
        
        const requestOptions4 = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json' // Add Accept header like in cURL
          },
          body: formData4
        };
        
        console.log('Trying Approach 4: JSON as File object with Accept header');
        response = await fetch(apiEndpoint, requestOptions4);
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('All approaches failed. Final error response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText
        });
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || 'Failed to create event' };
        }
        
        throw new Error(errorData.message || 'Failed to create event');
      }
      
      console.log('✅ SUCCESS! Event creation worked with one of the approaches');
      const data = await response.json();
      
      // Handle recurring events response differently
      if (newEvent.isRecurring) {
        const recurringResponse = data.data;
        const ticketCount = newEvent.tickets?.length || 0;
        const isVirtual = newEvent.isVirtualEvent;
        let message = `✅ Successfully created ${recurringResponse.totalEventsCreated} recurring events`;
        if (ticketCount > 0) message += ` with ${ticketCount} ticket type(s) each`;
        if (isVirtual) message += ` with virtual event configuration`;
        message += '!';
        setSuccessMessage(message);
        console.log('Recurring events created:', recurringResponse);
      } else {
        const createdEvent = data.data;
        
        // Create tickets if any were defined
        if (newEvent.tickets && newEvent.tickets.length > 0) {
        setSuccessMessage(`Event created! Creating ${newEvent.tickets.length} ticket type(s)...`);
        
        let ticketsCreated = 0;
        const ticketErrors = [];
        
        for (const ticket of newEvent.tickets) {
          try {
            // Convert datetime-local to UTC Instant using event's timezone
            const ticketData = {
              name: ticket.name,
              description: ticket.description,
              price: parseFloat(ticket.price),
              currency: ticket.currency,
              ticketType: ticket.ticketType,
              taxPercentage: parseFloat(ticket.taxPercentage) || 0,
              serviceCharge: parseFloat(ticket.serviceCharge) || 0,
              availableQuantity: parseInt(ticket.availableQuantity),
              active: ticket.active !== false,
              displayOrder: ticket.displayOrder,
              saleStartDate: ticket.saleStartDate ? datetimeLocalToISO(ticket.saleStartDate, newEvent.timezone) : null,
              saleEndDate: ticket.saleEndDate ? datetimeLocalToISO(ticket.saleEndDate, newEvent.timezone) : null,
              earlyBirdPrice: ticket.earlyBirdPrice ? parseFloat(ticket.earlyBirdPrice) : null,
              earlyBirdEndDate: ticket.earlyBirdEndDate ? datetimeLocalToISO(ticket.earlyBirdEndDate, newEvent.timezone) : null,
              groupDiscountPrice: ticket.groupDiscountPrice ? parseFloat(ticket.groupDiscountPrice) : null,
              minimumGroupSize: ticket.minimumGroupSize ? parseInt(ticket.minimumGroupSize) : null
            };
            
            const ticketResponse = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/${createdEvent.id}/ticket-prices`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(ticketData)
            });
            
            if (ticketResponse.ok) {
              ticketsCreated++;
              setSuccessMessage(`Event created! Tickets created: ${ticketsCreated}/${newEvent.tickets.length}`);
            } else {
              const errorData = await ticketResponse.json();
              ticketErrors.push(`${ticket.name}: ${errorData.message || 'Failed'}`);
            }
          } catch (ticketErr) {
            console.error('Error creating ticket:', ticketErr);
            ticketErrors.push(`${ticket.name}: ${ticketErr.message}`);
          }
        }
        
        // Show final status
        if (ticketErrors.length === 0) {
          setSuccessMessage(`✅ Event and ${ticketsCreated} ticket type(s) created successfully!`);
        } else {
          setSuccessMessage(`⚠️ Event created, but ${ticketErrors.length} ticket(s) failed. Check console for details.`);
          console.error('Ticket creation errors:', ticketErrors);
        }
        } else {
          setSuccessMessage('Event created successfully!');
        }
        
        // Create virtual event details if this is a virtual event
        if (newEvent.isVirtualEvent && newEvent.virtualEventDetails.streamingUrl) {
          try {
            setSuccessMessage('Event created! Configuring virtual event settings...');
            
            const virtualEventData = {
              streamingUrl: newEvent.virtualEventDetails.streamingUrl,
              chatUrl: newEvent.virtualEventDetails.chatUrl || null,
              accessWindowHours: newEvent.virtualEventDetails.accessWindowHours,
              allowReplay: newEvent.virtualEventDetails.allowReplay,
              replayDurationHours: newEvent.virtualEventDetails.replayDurationHours,
              isActive: true
            };
            
            const virtualEventResponse = await fetch(
              `${config.API_BASE_URL}/em/v1/admin/virtual-events/${createdEvent.id}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(virtualEventData)
              }
            );
            
            if (virtualEventResponse.ok) {
              setSuccessMessage('✅ Virtual event created and configured successfully!');
              console.log('Virtual event details created for event:', createdEvent.id);
            } else {
              const errorData = await virtualEventResponse.json();
              console.error('Failed to create virtual event details:', errorData);
              setSuccessMessage('⚠️ Event created but virtual event configuration failed. You can configure it later.');
            }
          } catch (virtualErr) {
            console.error('Error creating virtual event details:', virtualErr);
            setSuccessMessage('⚠️ Event created but virtual event configuration failed. You can configure it later.');
          }
        }
      }
      
      // Reset form
      setNewEvent({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        eventType: 'CONFERENCE',
        capacity: 100000,
        location: '',
        image: null,
        eligibleTeams: [],
        timezone: 'America/Los_Angeles',
        venue: null,
        venueId: null,
        tickets: [],
        minPinLevelRank: null,
        isRecurring: false,
        recurrencePattern: {
          frequency: 'WEEKLY',
          interval: 1,
          daysOfWeek: [],
          daysOfMonth: [],
          excludeDates: [],
          maxOccurrences: 10,
          endDate: null
        },
        isVirtualEvent: false,
        virtualEventDetails: {
          streamingUrl: '',
          chatUrl: '',
          accessWindowHours: 4,
          allowReplay: false,
          replayDurationHours: null,
          isActive: true
        }
      });
      setCustomVenueName('');
      setSaveNewVenue(true);
      
      // Hide form and refresh events
      setShowAddForm(false);
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Refresh events to get updated ticket info
      await refreshLoadedData();
      
    } catch (err) {
      console.error('Error creating event:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Update an existing event
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    
    try {
      setActionLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      // Save venue to database if checkbox is checked and we're using a new venue
      let savedVenueId = editEvent.venueId; // Use existing venueId if already set
      
      if (!useExistingVenueEdit && editEvent.venue && saveNewVenueEdit) {
        try {
          // Update venue name with custom name if provided
          const venueToSave = {
            ...editEvent.venue,
            name: customVenueNameEdit || editEvent.venue.name
          };
          const savedVenue = await saveVenueToDatabase(venueToSave, token);
          savedVenueId = savedVenue.id;  // Capture the saved venue ID
          console.log('Venue saved with ID:', savedVenueId);
          setSuccessMessage('Venue saved successfully! Updating event...');
        } catch (venueErr) {
          // Log the error but continue with event update
          console.warn('Failed to save venue, continuing with event update:', venueErr);
        }
      }
      
      // Prepare request data - convert datetime-local to ISO UTC
      const requestData = {
        name: editEvent.name,
        description: editEvent.description,
        startDate: datetimeLocalToISO(editEvent.startDate, editEvent.timezone),
        endDate: datetimeLocalToISO(editEvent.endDate, editEvent.timezone),
        eventType: editEvent.eventType,
        capacity: parseInt(editEvent.capacity),
        location: editEvent.location,
        status: editEvent.status,
        timezone: editEvent.timezone,
        eligibleTeams: editEvent.eligibleTeams || []
      };
      
      // Include venueId if a venue is associated (either existing or newly saved)
      if (savedVenueId) {
        requestData.venueId = savedVenueId;
        console.log('Including venueId in event update:', savedVenueId);
      }
      
      // Include minPinLevelRank if event type is EXCLUSIVE
      if (editEvent.eventType === 'EXCLUSIVE' && editEvent.minPinLevelRank) {
        requestData.minPinLevelRank = parseInt(editEvent.minPinLevelRank);
        console.log('Including minPinLevelRank for EXCLUSIVE event:', requestData.minPinLevelRank);
      }
      
      // Include isVirtualEvent flag
      requestData.isVirtualEvent = editEvent.isVirtualEvent || false;

      requestData.stagetimerRoomId = editEvent.stagetimerRoomId || '';

      // Send hotel booking link (empty string clears it on the backend)
      requestData.hotelBookingUrl = (editEvent.hotelBookingUrl || '').trim();

      // Create FormData with JSON as Blob (as backend expects)
      const formData = new FormData();
      
      // Create JSON Blob with correct content type
      const jsonBlob = new Blob([JSON.stringify(requestData)], {
        type: 'application/json'
      });
      
      // Append JSON as Blob with application/json content type
      formData.append('request', jsonBlob);
      
      // Add image if selected
      if (editEvent.image) {
        formData.append('image', editEvent.image);
      }
      
      // Create request with explicit headers - DO NOT set Content-Type for multipart/form-data
      const requestOptions = {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Do NOT set Content-Type header - let browser set it automatically for FormData
        },
        body: formData
      };
      
      console.log('Updating event with FormData (JSON as Blob):', {
        eventId: currentEvent.id,
        hasImage: !!editEvent.image,
        requestDataString: JSON.stringify(requestData),
        jsonBlobType: jsonBlob.type,
        jsonBlobSize: jsonBlob.size,
        formDataEntries: Array.from(formData.entries()).map(([key, value]) => [
          key, 
          value instanceof File ? `File: ${value.name} (${value.type})` : 
          value instanceof Blob ? `Blob: ${value.type} (${value.size} bytes)` : value
        ])
      });
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/${currentEvent.id}`, requestOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update event error response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText
        });
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || 'Failed to update event' };
        }
        
        throw new Error(errorData.message || 'Failed to update event');
      }
      
      const data = await response.json();
      
      // Update or create virtual event details if this is a virtual event
      if (editEvent.isVirtualEvent && editEvent.virtualEventDetails?.streamingUrl) {
        try {
          setSuccessMessage('Event updated! Configuring virtual event settings...');
          
          const virtualEventData = {
            streamingUrl: editEvent.virtualEventDetails.streamingUrl,
            chatUrl: editEvent.virtualEventDetails.chatUrl || '',
            accessWindowHours: editEvent.virtualEventDetails.accessWindowHours || 4,
            allowReplay: editEvent.virtualEventDetails.allowReplay || false,
            replayDurationHours: editEvent.virtualEventDetails.allowReplay ? 
              (editEvent.virtualEventDetails.replayDurationHours || 24) : null,
            isActive: true
          };
          
          console.log('Updating virtual event configuration:', virtualEventData);
          
          const virtualResponse = await fetch(
            `${config.API_BASE_URL}/em/v1/admin/virtual-events/${currentEvent.id}`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(virtualEventData)
            }
          );
          
          if (!virtualResponse.ok) {
            // If update fails, try creating instead (event might not have virtual details yet)
            const createResponse = await fetch(
              `${config.API_BASE_URL}/em/v1/admin/virtual-events/${currentEvent.id}`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(virtualEventData)
              }
            );
            
            if (!createResponse.ok) {
              console.error('Failed to create virtual event configuration');
            }
          }
          
          console.log('Virtual event configuration updated successfully');
        } catch (virtualErr) {
          console.error('Error updating virtual event configuration:', virtualErr);
          // Don't fail the whole update if virtual event config fails
        }
      }
      
      // Hide form and refresh events
      setShowEditForm(false);
      setCurrentEvent(null);
      setSuccessMessage('Event updated successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Update the event in the list
      setEvents(prev => prev.map(event =>
        event.id === data.data.id ? data.data : event
      ));
      // Keep month counts/buckets correct (e.g. if the event's date changed).
      refreshLoadedData();
      
    } catch (err) {
      console.error('Error updating event:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Delete an event
  const handleDeleteEvent = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/${currentEvent.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete event');
      }
      
      // Hide confirmation and refresh events
      setShowDeleteConfirm(false);
      setCurrentEvent(null);
      setSuccessMessage('Event deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Remove the event from the list
      setEvents(prev => prev.filter(event => event.id !== currentEvent.id));
      // Update the month count for the header.
      refreshLoadedData();
      
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Helper function to format date string to readable date
  const formatDate = (dateString, timezone = 'America/Los_Angeles') => {
    if (!dateString) return 'No date specified';
    return formatEventDate(dateString, timezone);
  };

  // Helper function to format price
  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  // Helper function to get eligible teams display
  const getEligibleTeamsDisplay = (eligibleTeams) => {
    if (!eligibleTeams || eligibleTeams.length === 0) {
      return 'No team restrictions';
    }
    
    if (eligibleTeams.includes('all')) {
      return 'All teams eligible';
    }
    
    const teamNames = eligibleTeams.map(teamId => {
      const team = teams.find(t => t.name === teamId || t.id === teamId);
      return team ? team.name : teamId;
    });
    
    if (teamNames.length <= 2) {
      return teamNames.join(', ');
    }
    
    return `${teamNames.slice(0, 2).join(', ')} +${teamNames.length - 2} more`;
  };
  
  // Handle event card click - open details modal
  const handleEventCardClick = async (event) => {
    setSelectedEventForDetails(event);
    setShowEventDetailsModal(true);
    setModalVirtualDetails(null);
    
    if (event.isVirtualEvent) {
      setModalVirtualDetailsLoading(true);
      try {
        const response = await virtualEventService.getVirtualEvent(event.id);
        const details = response.data || response;
        setModalVirtualDetails(details);
      } catch (err) {
        setModalVirtualDetails(null);
      } finally {
        setModalVirtualDetailsLoading(false);
      }
    }
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterTeam('all');
    setFilterStatus('all');
    setFilterDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
  };
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           filterTeam !== 'all' || 
           filterStatus !== 'all' || 
           filterDateRange !== 'all';
  };

  // Helper function to get ticket price summary
  const getTicketPriceSummary = (ticketPriceInfos) => {
    if (!ticketPriceInfos || ticketPriceInfos.length === 0) {
      return 'No pricing information';
    }

    // Get active tickets only
    const activeTickets = ticketPriceInfos.filter(ticket => ticket.active);
    
    if (activeTickets.length === 0) {
      return 'No active tickets';
    }

    // Sort by display order
    const sortedTickets = activeTickets.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    
    if (sortedTickets.length === 1) {
      const ticket = sortedTickets[0];
      return `${ticket.name}: ${formatPrice(ticket.price, ticket.currency)}`;
    }

    // Show price range
    const prices = sortedTickets.map(t => t.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const currency = sortedTickets[0].currency || 'USD';

    if (minPrice === maxPrice) {
      return `${formatPrice(minPrice, currency)}`;
    }

    return `${formatPrice(minPrice, currency)} - ${formatPrice(maxPrice, currency)}`;
  };

  // Helper function to check if early bird pricing is active
  const isEarlyBirdActive = (ticket) => {
    if (!ticket.earlyBirdEndDate) return false;
    
    try {
      const now = new Date();
      // Backend now returns UTC Instant as ISO string (e.g., "2025-06-15T23:59:00.000Z")
      const earlyBirdEndDate = new Date(ticket.earlyBirdEndDate);
      
      if (isNaN(earlyBirdEndDate.getTime())) {
        console.warn('Invalid earlyBirdEndDate:', ticket.earlyBirdEndDate);
        return false;
      }
      
      // Check if we have an early bird start date
      if (ticket.earlyBirdStartDate) {
        const earlyBirdStartDate = new Date(ticket.earlyBirdStartDate);
        if (!isNaN(earlyBirdStartDate.getTime()) && now < earlyBirdStartDate) {
          return false;
        }
      }
      
      // Compare UTC timestamps
      return now <= earlyBirdEndDate;
    } catch (error) {
      console.error('Error checking early bird status:', error);
      return false;
    }
  };

  // Post-Event Content Functions
  const handlePostEventContent = (event) => {
    setPostEventContentEvent(event);
    setShowPostEventContent(true);
  };

  // Virtual Config Modal Functions
  const handleVirtualConfig = async (event) => {
    setVirtualConfigEvent(event);
    setShowVirtualConfigModal(true);
    setVirtualConfigLoading(true);
    setVirtualConfigData(null);
    try {
      const data = await virtualEventService.getVirtualEvent(event.id);
      const details = data.data || data;
      setVirtualConfigData(details);
      setVirtualConfigForm({
        streamingUrl: details.streamingUrl || '',
        chatUrl: details.chatUrl || '',
        accessWindowHours: details.accessWindowHours || 4,
        allowReplay: details.allowReplay || false,
        replayDurationHours: details.replayDurationHours || null,
        isActive: details.isActive !== false
      });
    } catch (err) {
      setVirtualConfigData(null);
      setVirtualConfigForm({
        streamingUrl: '',
        chatUrl: '',
        accessWindowHours: 4,
        allowReplay: false,
        replayDurationHours: null,
        isActive: true
      });
    } finally {
      setVirtualConfigLoading(false);
    }
  };

  const handleSaveVirtualConfig = async () => {
    if (!virtualConfigEvent) return;
    setVirtualConfigSaving(true);
    try {
      const requestData = {
        streamingUrl: virtualConfigForm.streamingUrl,
        chatUrl: virtualConfigForm.chatUrl || null,
        accessWindowHours: virtualConfigForm.accessWindowHours,
        allowReplay: virtualConfigForm.allowReplay,
        replayDurationHours: virtualConfigForm.allowReplay ? virtualConfigForm.replayDurationHours : null,
        isActive: virtualConfigForm.isActive
      };

      if (virtualConfigData) {
        await virtualEventService.updateVirtualEvent(virtualConfigEvent.id, requestData);
      } else {
        await virtualEventService.createVirtualEvent(virtualConfigEvent.id, requestData);
      }
      setSuccessMessage('Virtual event configuration saved successfully!');
      setShowVirtualConfigModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving virtual config:', err);
      if (!virtualConfigData) {
        try {
          const requestData = {
            streamingUrl: virtualConfigForm.streamingUrl,
            chatUrl: virtualConfigForm.chatUrl || null,
            accessWindowHours: virtualConfigForm.accessWindowHours,
            allowReplay: virtualConfigForm.allowReplay,
            replayDurationHours: virtualConfigForm.allowReplay ? virtualConfigForm.replayDurationHours : null,
            isActive: virtualConfigForm.isActive
          };
          await virtualEventService.createVirtualEvent(virtualConfigEvent.id, requestData);
          setSuccessMessage('Virtual event configuration created successfully!');
          setShowVirtualConfigModal(false);
          setTimeout(() => setSuccessMessage(''), 3000);
        } catch (createErr) {
          setError('Failed to save virtual event configuration.');
        }
      } else {
        setError('Failed to save virtual event configuration.');
      }
    } finally {
      setVirtualConfigSaving(false);
    }
  };

  const handleToggleVirtualActive = async (event) => {
    try {
      const data = await virtualEventService.getVirtualEvent(event.id);
      const details = data.data || data;
      const requestData = {
        streamingUrl: details.streamingUrl,
        chatUrl: details.chatUrl,
        accessWindowHours: details.accessWindowHours,
        allowReplay: details.allowReplay,
        replayDurationHours: details.replayDurationHours,
        isActive: !details.isActive
      };
      await virtualEventService.updateVirtualEvent(event.id, requestData);
      setSuccessMessage(`Virtual event ${requestData.isActive ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling virtual event status:', err);
      setError('Failed to toggle virtual event status.');
    }
  };

  // Ticket Management Functions
  const handleManageTickets = async (event) => {
    setCurrentEventForTickets(event);
    setShowTicketManagement(true);
    await fetchTicketPrices(event.id);
  };

  const fetchTicketPrices = async (eventId) => {
    try {
      setLoadingTickets(true);
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/${eventId}/ticket-prices`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch ticket prices');

      const data = await response.json();
      setTicketPrices(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error('Error fetching ticket prices:', err);
      setError(err.message);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleAddTicket = () => {
    setEditingTicket(null);
    
    // Get event timezone and dates for pre-filling
    const eventTimezone = currentEventForTickets?.timezone || 'America/Los_Angeles';
    
    // Pre-fill sale start date with current time in event's timezone
    let saleStartDate = '';
    try {
      const nowISO = new Date().toISOString(); // Current time in UTC
      saleStartDate = isoToDatetimeLocal(nowISO, eventTimezone);
    } catch (error) {
      console.error('Error formatting current time:', error);
    }
    
    // Pre-fill sale end date with event's end date in event's timezone
    let saleEndDate = '';
    if (currentEventForTickets?.endDate) {
      try {
        saleEndDate = isoToDatetimeLocal(currentEventForTickets.endDate, eventTimezone);
      } catch (error) {
        console.error('Error formatting event end date:', error);
      }
    }
    
    setTicketFormData({
      name: '',
      description: '',
      price: '',
      currency: 'USD',
      ticketType: 'REGULAR',
      taxPercentage: 0,
      serviceCharge: 0,
      availableQuantity: '',
      active: true,
      saleStartDate: saleStartDate,  // Pre-filled with current time in event timezone
      saleEndDate: saleEndDate,      // Pre-filled with event end date in event timezone
      earlyBirdPrice: '',
      earlyBirdEndDate: '',
      groupDiscountPrice: '',
      minimumGroupSize: '',
      displayOrder: 1
    });
    setShowTicketForm(true);
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    
    // Get event timezone for proper conversion
    const eventTimezone = currentEventForTickets?.timezone || 'America/Los_Angeles';
    
    // Convert UTC Instant to datetime-local format in event's timezone
    const formatDateForInput = (utcDateString) => {
      if (!utcDateString) return '';
      
      try {
        // Use the existing isoToDatetimeLocal utility function
        return isoToDatetimeLocal(utcDateString, eventTimezone);
      } catch (error) {
        console.error('Error formatting date for input:', error, utcDateString);
        return '';
      }
    };

    setTicketFormData({
      name: ticket.name || '',
      description: ticket.description || '',
      price: ticket.price || '',
      currency: ticket.currency || 'USD',
      ticketType: ticket.ticketType || 'REGULAR',
      taxPercentage: ticket.taxPercentage || 0,
      serviceCharge: ticket.serviceCharge || 0,
      availableQuantity: ticket.availableQuantity || 100000,  // Default to 100000 if not set
      active: ticket.active !== false,
      saleStartDate: formatDateForInput(ticket.saleStartDate),
      saleEndDate: formatDateForInput(ticket.saleEndDate),
      earlyBirdPrice: ticket.earlyBirdPrice || '',
      earlyBirdEndDate: formatDateForInput(ticket.earlyBirdEndDate),
      groupDiscountPrice: ticket.groupDiscountPrice || '',
      minimumGroupSize: ticket.minimumGroupSize || '',
      displayOrder: ticket.displayOrder || 1
    });
    setShowTicketForm(true);
  };

  const handleTicketFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTicketFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setActionLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      // Get event timezone for proper conversion
      const eventTimezone = currentEventForTickets?.timezone || 'America/Los_Angeles';

      const submitData = {
        ...ticketFormData,
        price: parseFloat(ticketFormData.price),
        taxPercentage: parseFloat(ticketFormData.taxPercentage),
        serviceCharge: parseFloat(ticketFormData.serviceCharge),
        availableQuantity: parseInt(ticketFormData.availableQuantity),
        earlyBirdPrice: ticketFormData.earlyBirdPrice ? parseFloat(ticketFormData.earlyBirdPrice) : null,
        groupDiscountPrice: ticketFormData.groupDiscountPrice ? parseFloat(ticketFormData.groupDiscountPrice) : null,
        minimumGroupSize: ticketFormData.minimumGroupSize ? parseInt(ticketFormData.minimumGroupSize) : null,
        displayOrder: parseInt(ticketFormData.displayOrder),
        // Convert datetime-local to UTC Instant using event's timezone
        saleStartDate: ticketFormData.saleStartDate ? datetimeLocalToISO(ticketFormData.saleStartDate, eventTimezone) : null,
        saleEndDate: ticketFormData.saleEndDate ? datetimeLocalToISO(ticketFormData.saleEndDate, eventTimezone) : null,
        earlyBirdEndDate: ticketFormData.earlyBirdEndDate ? datetimeLocalToISO(ticketFormData.earlyBirdEndDate, eventTimezone) : null
      };

      let response;
      if (editingTicket) {
        // Update existing ticket
        response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/ticket-prices/${editingTicket.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(submitData)
        });
      } else {
        // Create new ticket
        response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/${currentEventForTickets.id}/ticket-prices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(submitData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save ticket');
      }

      setShowTicketForm(false);
      setSuccessMessage(editingTicket ? 'Ticket updated successfully!' : 'Ticket created successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Refresh ticket list and events
      await fetchTicketPrices(currentEventForTickets.id);
      await refreshLoadedData();
      
    } catch (err) {
      console.error('Error saving ticket:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket type?')) return;
    
    try {
      setActionLoading(true);
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/ticket-prices/${ticketId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete ticket');
      }

      setSuccessMessage('Ticket deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Refresh ticket list and events
      await fetchTicketPrices(currentEventForTickets.id);
      await refreshLoadedData();
      
    } catch (err) {
      console.error('Error deleting ticket:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleTicketActive = async (ticketId, currentStatus) => {
    try {
      setActionLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/ticket-prices/${ticketId}/active?active=${!currentStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to toggle ticket status');
      }

      setSuccessMessage(`Ticket ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh ticket list and events
      await fetchTicketPrices(currentEventForTickets.id);
      await refreshLoadedData();
      
    } catch (error) {
      console.error('Error toggling ticket status:', error);
      setError(error.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  // RSVP Management Functions
  const handleViewRSVPs = async (event) => {
    setCurrentEventForRSVP(event);
    setShowRSVPManagement(true);
    setRSVPActiveTab('summary');
    await Promise.all([
      fetchRSVPSummary(event.id),
      fetchRSVPData(event.id)
    ]);
  };

  const fetchRSVPSummary = async (eventId) => {
    try {
      setLoadingRSVPs(true);
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/rsvp/${eventId}/summary`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch RSVP summary');
      }

      const data = await response.json();
      setRSVPSummary(data.data);
    } catch (err) {
      console.error('Error fetching RSVP summary:', err);
      setError(err.message);
    } finally {
      setLoadingRSVPs(false);
    }
  };

  const fetchRSVPData = async (eventId) => {
    try {
      setLoadingRSVPs(true);
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/rsvp/${eventId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch RSVP data');
      }

      const data = await response.json();
      setRSVPData(data.data || []);
    } catch (err) {
      console.error('Error fetching RSVP data:', err);
      setError(err.message);
    } finally {
      setLoadingRSVPs(false);
    }
  };

  const formatRSVPDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getRSVPStatusColor = (status) => {
    switch (status) {
      case 'GOING': return '#28a745';
      case 'NOT_GOING': return '#dc3545';
      case 'MAYBE': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getRSVPStatusIcon = (status) => {
    switch (status) {
      case 'GOING': return faCheck;
      case 'NOT_GOING': return faTimes;
      case 'MAYBE': return faEye;
      default: return faUserCheck;
    }
  };

  // User Profile Functions
  const handleViewUserProfile = async (userId) => {
    try {
      setLoadingUserProfile(true);
      setShowUserProfile(true);
      setUserProfile(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${config.API_BASE_URL}/um/v1/user/profile?userId=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch user profile');
      }

      const data = await response.json();
      setUserProfile(data.data);
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err.message);
    } finally {
      setLoadingUserProfile(false);
    }
  };

  const closeUserProfile = () => {
    setShowUserProfile(false);
    setUserProfile(null);
  };

  // Helper function to filter events based on search and filters
  const filterEvents = (events) => {
    return events.filter(event => {
      // Search query filter (name, location, venue name, description)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          event.name.toLowerCase().includes(query) ||
          (event.description && event.description.toLowerCase().includes(query)) ||
          (event.location && event.location.toLowerCase().includes(query)) ||
          (event.venue && event.venue.name && event.venue.name.toLowerCase().includes(query)) ||
          (event.venue && event.venue.city && event.venue.city.toLowerCase().includes(query));
        
        if (!matchesSearch) return false;
      }
      
      // Team filter
      if (filterTeam !== 'all') {
        if (!event.eligibleTeams || event.eligibleTeams.length === 0) return false;
        if (!event.eligibleTeams.includes('all') && !event.eligibleTeams.includes(filterTeam)) {
          return false;
        }
      }
      
      // Status filter
      if (filterStatus !== 'all' && event.status !== filterStatus) {
        return false;
      }
      
      // Date range filter
      if (filterDateRange !== 'all') {
        const eventDate = new Date(event.startDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        switch (filterDateRange) {
          case 'today':
            const todayEnd = new Date(today);
            todayEnd.setHours(23, 59, 59, 999);
            if (eventDate < today || eventDate > todayEnd) return false;
            break;
            
          case 'week':
            const weekEnd = new Date(today);
            weekEnd.setDate(weekEnd.getDate() + 7);
            if (eventDate < today || eventDate > weekEnd) return false;
            break;
            
          case 'month':
            const monthEnd = new Date(today);
            monthEnd.setMonth(monthEnd.getMonth() + 1);
            if (eventDate < today || eventDate > monthEnd) return false;
            break;
            
          case 'custom':
            if (customStartDate) {
              const startDate = new Date(customStartDate);
              startDate.setHours(0, 0, 0, 0);
              if (eventDate < startDate) return false;
            }
            if (customEndDate) {
              const endDate = new Date(customEndDate);
              endDate.setHours(23, 59, 59, 999);
              if (eventDate > endDate) return false;
            }
            break;
        }
      }
      
      return true;
    });
  };
  
  // Helper function to format month-year header
  const formatMonthYear = (month, year) => {
    const date = new Date(year, month);
    const now = new Date();
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
    const monthName = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    return isCurrentMonth ? `${monthName} (Current Month)` : monthName;
  };

  // A single event card (compact: Tickets + Edit primary, everything else in the ⋮ menu)
  const renderEventCard = (event, style) => (
    <div
      key={event.id}
      className="event-card"
      onClick={() => handleEventCardClick(event)}
      style={style !== 'default' ? { opacity: 0.75, filter: 'grayscale(20%)' } : {}}
    >
      <div className="event-card-header">
        {event.imageUrl ? (
          <img
            src={getOptimizedImageUrl(event.imageUrl)}
            alt={event.name}
            className="event-image"
            data-original-src={event.imageUrl}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
            onLoad={handleImageLoad}
          />
        ) : null}
        <div className="event-image-placeholder" style={{ display: event.imageUrl ? 'none' : 'flex' }}>
          <FontAwesomeIcon icon={faCalendarAlt} />
        </div>
      </div>

      <div className="event-card-body">
        <h3>
          {event.name}
          {event.isVirtualEvent && (
            <span className="virtual-event-badge"><FontAwesomeIcon icon={faVideo} /> Virtual</span>
          )}
        </h3>
        <p className="event-description">{event.description}</p>
        <div className="event-details">
          {event.venue ? (
            <div className="event-venue-info">
              <p className="venue-name">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <strong>{event.venue.name}</strong>
                {event.venue.rating && (
                  <span style={{ marginLeft: '8px', color: '#f57c00', fontSize: '13px' }}>
                    ⭐ {event.venue.rating}
                  </span>
                )}
              </p>
              <p className="venue-address" style={{ fontSize: '13px', color: '#666', marginLeft: '20px' }}>
                {event.venue.city && event.venue.state
                  ? `${event.venue.city}, ${event.venue.state}`
                  : event.venue.formattedAddress}
              </p>
              {event.venue.phoneNumber && (
                <p className="venue-phone" style={{ fontSize: '12px', color: '#666', marginLeft: '20px' }}>
                  📞 {event.venue.phoneNumber}
                </p>
              )}
            </div>
          ) : (
            <p><FontAwesomeIcon icon={faMapMarkerAlt} /> {event.location || 'No location specified'}</p>
          )}
          <p><FontAwesomeIcon icon={faCalendarAlt} /> {formatDate(event.startDate, event.timezone)}</p>
          {event.endDate && event.endDate !== event.startDate && (
            <p><FontAwesomeIcon icon={faCalendarAlt} /> Ends: {formatDate(event.endDate, event.timezone)}</p>
          )}
          <p><FontAwesomeIcon icon={faUsers} /> {getEligibleTeamsDisplay(event.eligibleTeams)}</p>
          <div className="ticket-pricing-info">
            <p><FontAwesomeIcon icon={faDollarSign} /> {getTicketPriceSummary(event.ticketPriceInfos)}</p>
            {event.ticketPriceInfos && event.ticketPriceInfos.length > 0 && (
              <div className="ticket-types-summary">
                {event.ticketPriceInfos
                  .filter(ticket => ticket.active)
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .slice(0, 3)
                  .map(ticket => (
                  <div key={ticket.id} className="ticket-type-chip">
                    <span className="ticket-type-name">{ticket.name}</span>
                    {isEarlyBirdActive(ticket) && (
                      <span className="early-bird-badge">Early Bird</span>
                    )}
                    <span className="ticket-type-price">
                      {isEarlyBirdActive(ticket) ?
                        formatPrice(ticket.earlyBirdPrice, ticket.currency) :
                        formatPrice(ticket.price, ticket.currency)
                      }
                    </span>
                    {ticket.availableQuantity !== undefined && (
                      <span className="ticket-quantity">({ticket.availableQuantity} left)</span>
                    )}
                  </div>
                ))}
                {event.ticketPriceInfos.filter(t => t.active).length > 3 && (
                  <div className="more-tickets">
                    +{event.ticketPriceInfos.filter(t => t.active).length - 3} more
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="event-card-actions" onClick={(e) => e.stopPropagation()}>
          <button className="manage-tickets-button" onClick={() => handleManageTickets(event)}>
            <FontAwesomeIcon icon={faTicketAlt} /> Tickets
          </button>
          <button className="edit-button" onClick={() => handleEditEvent(event)}>
            <FontAwesomeIcon icon={faEdit} /> Edit
          </button>
          <button className="more-actions-button" title="More actions" onClick={(e) => openCardMenu(e, event)}>
            <FontAwesomeIcon icon={faEllipsisV} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderEvents = () => {
    if (loading) {
      return <div className="loading-indicator">Loading events...</div>;
    }
    if (error) {
      return <div className="error-message">{error}</div>;
    }
    if (monthSummaries.length === 0 && events.length === 0) {
      return <div className="no-events">No events found. Click "Add Event" to create one.</div>;
    }

    // Filters apply to the months already loaded.
    const filteredEvents = filterEvents(events);
    const filtersActive = hasActiveFilters();

    const eventsByMonthKey = {};
    filteredEvents.forEach(ev => {
      const k = isoMonthKey(ev.startDate);
      (eventsByMonthKey[k] = eventsByMonthKey[k] || []).push(ev);
    });
    Object.values(eventsByMonthKey).forEach(list =>
      list.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));

    // Order months: current first, then future ascending, then past descending.
    const ck = currentMonthKey();
    const summaryCount = new Map(monthSummaries.map(m => [m.month, m.count]));
    const allKeys = new Set([...summaryCount.keys(), ...loadedMonths, ck]);
    const future = [...allKeys].filter(k => k > ck).sort();
    const past = [...allKeys].filter(k => k < ck).sort().reverse();
    const orderedKeys = [...(allKeys.has(ck) ? [ck] : []), ...future, ...past];

    const gradientMap = {
      default: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
      past: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)',
    };

    const renderMonthSection = (monthKey) => {
      const [year, monthNum] = monthKey.split('-').map(Number);
      const monthIndex0 = monthNum - 1;
      const isCollapsed = collapsedMonths.has(monthKey);
      const isLoadingMonth = loadingMonths.has(monthKey);
      const isLoadedMonth = loadedMonths.has(monthKey);
      const monthEvents = eventsByMonthKey[monthKey] || [];
      const count = summaryCount.has(monthKey) ? summaryCount.get(monthKey) : monthEvents.length;
      const style = monthKey >= ck ? 'default' : 'past';
      return (
        <div key={monthKey} className="month-section">
          <div
            className="month-header clickable"
            onClick={() => toggleMonthCollapse(monthKey)}
            style={{
              cursor: 'pointer',
              background: gradientMap[style] || gradientMap.default,
              ...(style !== 'default' && { opacity: 0.85 })
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  transition: 'transform 0.3s',
                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  color: '#fff'
                }}
              />
              <h3 style={{ color: '#fff' }}>{formatMonthYear(monthIndex0, year)}</h3>
            </div>
            <span className="event-count" style={{ color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.25)' }}>{count} event{count !== 1 ? 's' : ''}</span>
          </div>
          {!isCollapsed && (
            isLoadingMonth && !isLoadedMonth ? (
              <div className="loading-indicator" style={{ padding: '24px' }}>Loading events...</div>
            ) : monthEvents.length === 0 ? (
              <div className="no-events" style={{ padding: '24px' }}>
                {filtersActive ? 'No events match your search in this month.' : 'No events in this month.'}
              </div>
            ) : (
              <div className="events-grid">
                {monthEvents.map(event => renderEventCard(event, style))}
              </div>
            )
          )}
        </div>
      );
    };

    return (
      <div className="events-by-month-container">
        {orderedKeys.map(key => renderMonthSection(key))}
      </div>
    );
  };

  return (
    <div className="event-management-container">
      <div className="event-management-header-section">
        <h2>Admin Portal</h2>
        <button onClick={handleBackToDashboard} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>
      </div>
      
      {/* Tab Switcher */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          <FontAwesomeIcon icon={faCalendarAlt} /> Event Management
        </button>
        <button 
          className={`tab-btn ${activeTab === 'venues' ? 'active' : ''}`}
          onClick={() => setActiveTab('venues')}
        >
          <FontAwesomeIcon icon={faMapMarkerAlt} /> Venue Management
        </button>
      </div>
      
      {/* Events Tab Content */}
      {activeTab === 'events' && (
        <>
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}
          
          <div className="actions-bar">
            <button onClick={handleAddEvent} className="add-event-button">
              <FontAwesomeIcon icon={faCalendarPlus} /> Add New Event
            </button>
          </div>
          
          {/* Search and Filter Section */}
          <div className="search-filter-container">
            <div className="search-filter-header">
              <div className="search-box">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search events by name, venue, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="clear-search-icon"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
              
              <button
                className="toggle-filters-btn"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <FontAwesomeIcon icon={showAdvancedFilters ? faTimes : faPlus} />
                {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
              </button>
              
              {hasActiveFilters() && (
                <button
                  className="clear-all-filters-btn"
                  onClick={handleClearFilters}
                  title="Clear all filters"
                >
                  <FontAwesomeIcon icon={faTimes} /> Clear All
                </button>
              )}
            </div>
            
            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="advanced-filters">
                <div className="filter-row">
                  {/* Team Filter */}
                  <div className="filter-group">
                    <label htmlFor="filter-team">
                      <FontAwesomeIcon icon={faUsers} /> Team
                    </label>
                    <select
                      id="filter-team"
                      value={filterTeam}
                      onChange={(e) => setFilterTeam(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Teams</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Status Filter */}
                  <div className="filter-group">
                    <label htmlFor="filter-status">
                      <FontAwesomeIcon icon={faCalendarAlt} /> Status
                    </label>
                    <select
                      id="filter-status"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Status</option>
                      <option value="UPCOMING">Upcoming</option>
                      <option value="ONGOING">Ongoing</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                    </select>
                  </div>
                  
                  {/* Date Range Filter */}
                  <div className="filter-group">
                    <label htmlFor="filter-date-range">
                      <FontAwesomeIcon icon={faCalendarAlt} /> Date Range
                    </label>
                    <select
                      id="filter-date-range"
                      value={filterDateRange}
                      onChange={(e) => setFilterDateRange(e.target.value)}
                      className="filter-select"
                    >
                      <option value="all">All Dates</option>
                      <option value="today">Today</option>
                      <option value="week">Next 7 Days</option>
                      <option value="month">Next 30 Days</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                </div>
                
                {/* Custom Date Range */}
                {filterDateRange === 'custom' && (
                  <div className="filter-row custom-date-row">
                    <div className="filter-group">
                      <label htmlFor="custom-start-date">Start Date</label>
                      <input
                        type="date"
                        id="custom-start-date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="filter-date-input"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="custom-end-date">End Date</label>
                      <input
                        type="date"
                        id="custom-end-date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="filter-date-input"
                      />
                    </div>
                  </div>
                )}
                
                {/* Active Filters Summary */}
                {hasActiveFilters() && (
                  <div className="active-filters-summary">
                    <strong>Active Filters:</strong>
                    {searchQuery && <span className="filter-tag">Search: "{searchQuery}"</span>}
                    {filterTeam !== 'all' && <span className="filter-tag">Team: {filterTeam}</span>}
                    {filterStatus !== 'all' && <span className="filter-tag">Status: {filterStatus}</span>}
                    {filterDateRange !== 'all' && (
                      <span className="filter-tag">
                        Date: {filterDateRange === 'custom' ? 'Custom Range' : filterDateRange}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
      
      {/* Create new event form */}
      {showAddForm && (
        <div className="add-event-form-container">
          <button
            type="button"
            className="form-back-link"
            onClick={() => setShowAddForm(false)}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Events
          </button>
          <h3>Add New Event</h3>

          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleCreateEvent} className="add-event-form">
            <div className="form-group">
              <label htmlFor="name">Event Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={newEvent.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={newEvent.description}
                onChange={handleInputChange}
              />
            </div>
            
            {/* Hide venue section for virtual events */}
            {!newEvent.isVirtualEvent && (
            <div className="form-section venue-section">
              <h4>📍 Event Location</h4>
              <p className="section-hint">Select an existing venue or search for a new one</p>
              
              {/* Toggle between existing venue and new venue */}
              <div className="venue-toggle-buttons" style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className={`venue-toggle-btn ${useExistingVenue ? 'active' : ''}`}
                  onClick={() => {
                    setUseExistingVenue(true);
                    setNewEvent(prev => ({
                      ...prev,
                      venue: null,
                      venueId: null,
                      location: '',
                      timezone: 'America/Los_Angeles'
                    }));
                  }}
                  style={{
                    padding: '10px 20px',
                    border: useExistingVenue ? '2px solid #5c6bc0' : '1px solid #ddd',
                    backgroundColor: useExistingVenue ? '#e8eaf6' : '#fff',
                    color: useExistingVenue ? '#5c6bc0' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: useExistingVenue ? '600' : '400',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📋 Select Existing Venue
                </button>
                <button
                  type="button"
                  className={`venue-toggle-btn ${!useExistingVenue ? 'active' : ''}`}
                  onClick={() => {
                    setUseExistingVenue(false);
                    setNewEvent(prev => ({
                      ...prev,
                      venue: null,
                      venueId: null,
                      location: '',
                      timezone: 'America/Los_Angeles'
                    }));
                  }}
                  style={{
                    padding: '10px 20px',
                    border: !useExistingVenue ? '2px solid #5c6bc0' : '1px solid #ddd',
                    backgroundColor: !useExistingVenue ? '#e8eaf6' : '#fff',
                    color: !useExistingVenue ? '#5c6bc0' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: !useExistingVenue ? '600' : '400',
                    transition: 'all 0.3s ease'
                  }}
                >
                  🔍 Search New Venue
                </button>
                {/* DV-260: Browse & Select aligned on the same row as the "Select Existing Venue" toggle */}
                {useExistingVenue && !loadingVenues && venues.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleOpenVenueModal('new')}
                    style={{
                      padding: '10px 20px',
                      border: '2px dashed #5c6bc0',
                      borderRadius: '6px',
                      backgroundColor: newEvent.venue ? '#e8eaf6' : '#f5f7fa',
                      color: '#5c6bc0',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {newEvent.venue ? (
                      <span>✓ {newEvent.venue.name} — Click to change</span>
                    ) : (
                      <span>📍 Browse &amp; Select Venue ({venues.length})</span>
                    )}
                  </button>
                )}
              </div>

              {/* Existing-venue loading / empty states */}
              {useExistingVenue && loadingVenues && (
                <div style={{ padding: '12px', color: '#666', fontStyle: 'italic' }}>
                  Loading venues...
                </div>
              )}
              {useExistingVenue && !loadingVenues && venues.length === 0 && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  color: '#856404'
                }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    ℹ️ No venues available. Click "Search New Venue" to create one.
                  </p>
                </div>
              )}
              
              {/* New Venue Search */}
              {!useExistingVenue && (
                <div className="form-group">
                  <label htmlFor="venue">Search for Venue *</label>
                  <GoogleMapsLocationPicker
                    key="new-venue-picker" // Force remount when switching modes
                    onLocationSelect={handleLocationSelect}
                    placeholder="Search for venue or click on the map..."
                  />
                </div>
              )}
              
              {/* Venue Info Display */}
              {newEvent.venue && (
                <>
                  {/* Editable Venue Name */}
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label htmlFor="custom-venue-name">
                      Venue Name *
                    </label>
                    <input
                      type="text"
                      id="custom-venue-name"
                      value={customVenueName}
                      onChange={(e) => setCustomVenueName(e.target.value)}
                      placeholder="Enter venue name"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        color: '#333',
                        fontFamily: 'inherit',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    <small className="form-hint" style={{ 
                      display: 'block', 
                      marginTop: '6px', 
                      fontSize: '12px', 
                      color: '#718096',
                      fontStyle: 'italic'
                    }}>
                      You can customize the venue name or keep the name from Google Maps
                    </small>
                  </div>
                  
                  <div className="venue-info-box">
                    <div className="venue-info-header">
                      <span className="venue-icon">📍</span>
                      <strong>{customVenueName || newEvent.venue.name}</strong>
                      {newEvent.venue.rating && (
                        <span className="venue-rating" style={{ marginLeft: '12px', color: '#f57c00', fontSize: '14px' }}>
                          ⭐ {newEvent.venue.rating} {newEvent.venue.userRatingsTotal && `(${newEvent.venue.userRatingsTotal} reviews)`}
                        </span>
                      )}
                    </div>
                    <div className="venue-info-details">
                      <p className="venue-address">
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {newEvent.venue.formattedAddress}
                      </p>
                      {newEvent.venue.city && newEvent.venue.state && (
                        <p className="venue-location-info">
                          {newEvent.venue.city}, {newEvent.venue.state} {newEvent.venue.postalCode}
                        </p>
                      )}
                      {newEvent.venue.timezone && (
                        <p className="venue-timezone">
                          🌍 Timezone: <strong>{newEvent.venue.timezone}</strong>
                        </p>
                      )}
                      {newEvent.venue.phoneNumber && (
                        <p className="venue-phone" style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>
                          📞 {newEvent.venue.phoneNumber}
                        </p>
                      )}
                      {newEvent.venue.website && (
                        <p className="venue-website" style={{ fontSize: '13px', marginTop: '6px' }}>
                          🔗 <a href={newEvent.venue.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                            Visit Website
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Save Venue Option - Only show if searching new venue */}
                  {!useExistingVenue && (
                    <div className="save-venue-option" style={{ 
                      marginTop: '12px', 
                      padding: '12px', 
                      backgroundColor: '#e8f5e9',
                      border: '1px solid #4caf50',
                      borderRadius: '6px'
                    }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#2e7d32'
                      }}>
                        <input
                          type="checkbox"
                          checked={saveNewVenue}
                          onChange={(e) => setSaveNewVenue(e.target.checked)}
                          style={{ 
                            marginRight: '10px',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        💾 Save this venue to the database for future events
                      </label>
                      <p style={{ 
                        margin: '8px 0 0 28px', 
                        fontSize: '12px', 
                        color: '#558b2f',
                        fontStyle: 'italic'
                      }}>
                        ✓ Recommended: This allows you to reuse this venue for other events
                      </p>
                    </div>
                  )}
                </>
              )}
              
              {/* Manual location input as fallback */}
              {!newEvent.venue && (
                <div className="form-group">
                  <label htmlFor="location">Or enter location manually</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={newEvent.location}
                    onChange={handleInputChange}
                    placeholder="Enter location manually if venue not found"
                  />
                </div>
              )}
            </div>
            )}
            
            {/* Show Virtual location indicator for virtual events */}
            {newEvent.isVirtualEvent && (
              <div className="form-group">
                <label>Event Location</label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  border: '2px solid #2196f3',
                  borderRadius: '6px',
                  color: '#1565c0',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🌐 Virtual Event - No physical venue required
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="timezone">Event Timezone *</label>
              <p className="field-hint">
                {newEvent.venue 
                  ? '✅ Auto-populated from venue location' 
                  : newEvent.isVirtualEvent
                    ? 'Select timezone for the virtual event'
                    : 'Select timezone manually or search for a venue above'}
              </p>
              <TimezoneSelector
                selectedTimezone={newEvent.timezone}
                onTimezoneChange={(tz) => setNewEvent(prev => ({ ...prev, timezone: tz }))}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date & Time ({newEvent.timezone || 'Select timezone'})</label>
                <input
                  type="datetime-local"
                  id="startDate"
                  name="startDate"
                  value={newEvent.startDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="endDate">End Date & Time ({newEvent.timezone || 'Select timezone'})</label>
                {!newEvent.endDate && newEvent.startDate && (
                  <p className="field-hint" style={{ color: '#28a745', fontSize: '12px', marginBottom: '4px' }}>
                    ✨ Will auto-populate when you select start date
                  </p>
                )}
                <input
                  type="datetime-local"
                  id="endDate"
                  name="endDate"
                  value={newEvent.endDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eventType">Event Type</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={newEvent.eventType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="CONFERENCE">Conference</option>
                  <option value="SEMINAR">Seminar</option>
                  <option value="OPEN">Open</option>
                  <option value="WORKSHOP">Workshop</option>
                  <option value="EXCLUSIVE">Exclusive</option>
                </select>
              </div>
            </div>
            
            {/* Pin Level Selection for EXCLUSIVE events */}
            {newEvent.eventType === 'EXCLUSIVE' && (
              <div className="form-group exclusive-pin-level-section">
                <label htmlFor="minPinLevelRank">
                  <span className="required-star">*</span> Minimum Pin Level Required
                  <span className="field-hint"> (Only users with this pin level or higher can see this event)</span>
                </label>
                <select
                  id="minPinLevelRank"
                  name="minPinLevelRank"
                  value={newEvent.minPinLevelRank || ''}
                  onChange={handleInputChange}
                  required
                  className="pin-level-select"
                >
                  <option value="">Select minimum pin level...</option>
                  <optgroup label="Percentage Pins">
                    <option value="1">3% and above</option>
                    <option value="2">6% and above</option>
                    <option value="3">9% and above</option>
                    <option value="4">12% and above</option>
                    <option value="5">15% and above</option>
                    <option value="6">18% and above</option>
                    <option value="7">21% and above</option>
                    <option value="8">25% and above</option>
                  </optgroup>
                  <optgroup label="Leadership Pins">
                    <option value="9">Silver Sponsor and above</option>
                    <option value="10">Silver and above</option>
                    <option value="11">Gold Sponsor and above</option>
                    <option value="12">Gold and above</option>
                    <option value="13">Platinum and above</option>
                    <option value="14">Ruby and above</option>
                    <option value="15">Sapphire and above</option>
                    <option value="16">Emerald and above</option>
                    <option value="17">Diamond and above</option>
                    <option value="18">Crown Ambassador only</option>
                  </optgroup>
                </select>
              </div>
            )}
            
            {/* Event Schedule (Stagetimer) */}
            <div className="form-group">
              <label htmlFor="stagetimerRoomId">Event Schedule (Optional)</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  id="stagetimerRoomId"
                  name="stagetimerRoomId"
                  value={newEvent.stagetimerRoomId || ''}
                  onChange={handleInputChange}
                  style={{ flex: 1 }}
                >
                  <option value="">-- No Schedule --</option>
                  {stagetimerRooms.map(room => (
                    <option key={room.roomId} value={room.roomId}>{room.roomName} ({room.roomId})</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={() => { setShowAddRoomModal(true); setAddRoomError(''); setNewRoomId(''); }} style={{ whiteSpace: 'nowrap' }}>
                  + Add Room
                </button>
              </div>
              <span className="field-hint">Link a stagetimer.io room to show event schedule to attendees</span>
            </div>

            {/* Hotel Booking Link */}
            <div className="form-group">
              <label htmlFor="hotelBookingUrl">Hotel Booking Link (Optional)</label>
              <input
                type="url"
                id="hotelBookingUrl"
                name="hotelBookingUrl"
                value={newEvent.hotelBookingUrl || ''}
                onChange={handleInputChange}
                placeholder="https://hotel-booking-link.com"
              />
              <span className="field-hint">If set, ticket holders can book a hotel from their ticket and ticket details pages</span>
            </div>

            {/* Virtual Event Configuration */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isVirtualEvent"
                  checked={newEvent.isVirtualEvent}
                  onChange={(e) => {
                    setNewEvent({
                      ...newEvent,
                      isVirtualEvent: e.target.checked,
                      location: e.target.checked ? 'Virtual' : newEvent.location,
                      venue: e.target.checked ? null : newEvent.venue
                    });
                    // Clear custom venue name when switching to virtual
                    if (e.target.checked) {
                      setCustomVenueName('');
                    }
                  }}
                />
                <span>This is a Virtual/Online Event</span>
              </label>
              <p className="field-hint">Check this if the event will be streamed online instead of a physical venue</p>
            </div>
            
            {/* Virtual Event Details - Conditional Section */}
            {newEvent.isVirtualEvent && (
              <div className="virtual-event-section">
                <h4>Virtual Event Configuration</h4>
                <p className="section-hint">Configure streaming and access settings for this virtual event</p>
                
                <div className="form-group">
                  <label htmlFor="streamingUrl">
                    <span className="required-star">*</span> Streaming URL
                  </label>
                  <textarea
                    id="streamingUrl"
                    name="virtualEventDetails.streamingUrl"
                    value={newEvent.virtualEventDetails.streamingUrl}
                    onChange={(e) => {
                      setNewEvent({
                        ...newEvent,
                        virtualEventDetails: {
                          ...newEvent.virtualEventDetails,
                          streamingUrl: e.target.value
                        }
                      });
                    }}
                    placeholder="Enter embedded video URL (e.g., Vimeo, YouTube embed URL)"
                    rows="3"
                    required={newEvent.isVirtualEvent}
                  />
                  <p className="field-hint">Example: https://player.vimeo.com/video/123456789</p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="chatUrl">Chat URL (optional)</label>
                  <textarea
                    id="chatUrl"
                    name="virtualEventDetails.chatUrl"
                    value={newEvent.virtualEventDetails.chatUrl}
                    onChange={(e) => {
                      setNewEvent({
                        ...newEvent,
                        virtualEventDetails: {
                          ...newEvent.virtualEventDetails,
                          chatUrl: e.target.value
                        }
                      });
                    }}
                    placeholder="Enter embedded chat URL (optional)"
                    rows="2"
                  />
                  <p className="field-hint">If provided, chat will be displayed alongside the video</p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="accessWindowHours">
                    Access Window (hours before event)
                  </label>
                  <input
                    type="number"
                    id="accessWindowHours"
                    name="virtualEventDetails.accessWindowHours"
                    value={newEvent.virtualEventDetails.accessWindowHours}
                    onChange={(e) => {
                      setNewEvent({
                        ...newEvent,
                        virtualEventDetails: {
                          ...newEvent.virtualEventDetails,
                          accessWindowHours: parseInt(e.target.value) || 4
                        }
                      });
                    }}
                    min="1"
                    max="24"
                    required={newEvent.isVirtualEvent}
                  />
                  <p className="field-hint">Users can access the event this many hours before it starts (1-24 hours)</p>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="virtualEventDetails.allowReplay"
                      checked={newEvent.virtualEventDetails.allowReplay}
                      onChange={(e) => {
                        setNewEvent({
                          ...newEvent,
                          virtualEventDetails: {
                            ...newEvent.virtualEventDetails,
                            allowReplay: e.target.checked,
                            replayDurationHours: e.target.checked ? 24 : null
                          }
                        });
                      }}
                    />
                    <span>Allow Replay After Event</span>
                  </label>
                  <p className="field-hint">Users can watch the recording after the event ends</p>
                </div>
                
                {newEvent.virtualEventDetails.allowReplay && (
                  <div className="form-group">
                    <label htmlFor="replayDurationHours">
                      Replay Duration (hours after event ends)
                    </label>
                    <input
                      type="number"
                      id="replayDurationHours"
                      name="virtualEventDetails.replayDurationHours"
                      value={newEvent.virtualEventDetails.replayDurationHours || ''}
                      onChange={(e) => {
                        setNewEvent({
                          ...newEvent,
                          virtualEventDetails: {
                            ...newEvent.virtualEventDetails,
                            replayDurationHours: parseInt(e.target.value) || null
                          }
                        });
                      }}
                      min="1"
                      placeholder="e.g., 24 for 1 day, 168 for 1 week"
                      required={newEvent.virtualEventDetails.allowReplay}
                    />
                    <p className="field-hint">How long after the event ends can users watch the replay</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="image">Event Image (optional)</label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleInputChange}
                className="file-input"
              />
              {newEvent.image && (
                <div className="image-preview">
                  <img 
                    src={URL.createObjectURL(newEvent.image)} 
                    alt="Event preview" 
                    className="preview-image"
                  />
                  <p className="preview-filename">{newEvent.image.name}</p>
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label><FontAwesomeIcon icon={faUsers} /> Eligible Teams</label>
              <div className="team-selection-container">
                {loadingTeams ? (
                  <div className="loading-teams">Loading teams...</div>
                ) : (
                  <>
                    <div className="team-checkbox-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          value="all"
                          checked={newEvent.eligibleTeams.includes('all')}
                          onChange={(e) => handleTeamSelection(e, 'new')}
                        />
                        <span className="checkmark"></span>
                        All Teams
                      </label>
                    </div>
                    {teams.map(team => (
                      <div key={team.id} className="team-checkbox-item">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            value={team.name}
                            checked={newEvent.eligibleTeams.includes(team.name)}
                            onChange={(e) => handleTeamSelection(e, 'new')}
                            disabled={newEvent.eligibleTeams.includes('all')}
                          />
                          <span className="checkmark"></span>
                          {team.name}
                        </label>
                      </div>
                    ))}
                    {teams.length === 0 && (
                      <div className="no-teams-message">No teams available</div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Ticket Types Section */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ marginBottom: 0 }}>
                  <FontAwesomeIcon icon={faTicketAlt} /> Ticket Types (Optional)
                </label>
                <button
                  type="button"
                  onClick={handleAddTicketToEvent}
                  className="add-ticket-button"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Ticket Type
                </button>
              </div>
              
              <p className="field-hint" style={{ fontSize: '13px', color: '#666', marginBottom: '16px', fontStyle: 'italic' }}>
                You can add ticket types now or later from the event management page.
              </p>
              
              {newEvent.tickets.length === 0 ? (
                <div style={{
                  padding: '32px',
                  backgroundColor: '#f5f5f5',
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: '#999'
                }}>
                  <FontAwesomeIcon icon={faTicketAlt} size="2x" style={{ marginBottom: '12px' }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>No ticket types added yet. Click "Add Ticket Type" to create one.</p>
                </div>
              ) : (
                <div className="tickets-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {newEvent.tickets.map((ticket, index) => (
                    <div key={ticket.id} style={{
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h5 style={{ margin: 0, color: '#1976d2' }}>
                          🎫 Ticket Type #{index + 1}
                        </h5>
                        <button
                          type="button"
                          onClick={() => handleRemoveTicketFromEvent(ticket.id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Remove
                        </button>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`ticket-name-${ticket.id}`}>Ticket Name *</label>
                          <input
                            type="text"
                            id={`ticket-name-${ticket.id}`}
                            value={ticket.name}
                            onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'name', e.target.value)}
                            placeholder="e.g., General Admission, VIP"
                            required={newEvent.tickets.length > 0}
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor={`ticket-type-${ticket.id}`}>Type</label>
                          <select
                            id={`ticket-type-${ticket.id}`}
                            value={ticket.ticketType}
                            onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'ticketType', e.target.value)}
                          >
                            <option value="REGULAR">Regular</option>
                            <option value="VIP">VIP</option>
                            <option value="PREMIUM">Premium</option>
                            <option value="EARLY_BIRD">Early Bird</option>
                            <option value="GROUP">Group</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`ticket-price-${ticket.id}`}>Price *</label>
                          <input
                            type="number"
                            step="0.01"
                            id={`ticket-price-${ticket.id}`}
                            value={ticket.price}
                            onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'price', e.target.value)}
                            placeholder="0.00"
                            required={newEvent.tickets.length > 0}
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor={`ticket-description-${ticket.id}`}>Description</label>
                        <textarea
                          id={`ticket-description-${ticket.id}`}
                          value={ticket.description}
                          onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'description', e.target.value)}
                          rows="2"
                          placeholder="Optional description for this ticket type"
                        />
                      </div>
                      
                      <details style={{ marginTop: '12px' }}>
                        <summary style={{ cursor: 'pointer', color: '#1976d2', fontSize: '14px', fontWeight: '600' }}>
                          ⚙️ Advanced Options (Sale Dates, Early Bird, etc.)
                        </summary>
                        <div style={{ marginTop: '12px', paddingLeft: '16px', borderLeft: '3px solid #1976d2' }}>
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor={`ticket-sale-start-${ticket.id}`}>Sale Start ({newEvent.timezone})</label>
                              <input
                                type="datetime-local"
                                id={`ticket-sale-start-${ticket.id}`}
                                value={ticket.saleStartDate}
                                onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'saleStartDate', e.target.value)}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor={`ticket-sale-end-${ticket.id}`}>Sale End ({newEvent.timezone})</label>
                              <input
                                type="datetime-local"
                                id={`ticket-sale-end-${ticket.id}`}
                                value={ticket.saleEndDate}
                                onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'saleEndDate', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor={`ticket-early-price-${ticket.id}`}>Early Bird Price</label>
                              <input
                                type="number"
                                step="0.01"
                                id={`ticket-early-price-${ticket.id}`}
                                value={ticket.earlyBirdPrice}
                                onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'earlyBirdPrice', e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor={`ticket-early-end-${ticket.id}`}>Early Bird End ({newEvent.timezone})</label>
                              <input
                                type="datetime-local"
                                id={`ticket-early-end-${ticket.id}`}
                                value={ticket.earlyBirdEndDate}
                                onChange={(e) => handleUpdateTicketInEvent(ticket.id, 'earlyBirdEndDate', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Recurring Event Section */}
            <div className="form-section recurring-section">
              <div className="recurring-header">
                <h4>🔄 Recurring Event</h4>
                <label className="recurring-toggle">
                  <input
                    type="checkbox"
                    checked={newEvent.isRecurring}
                    onChange={(e) => setNewEvent(prev => ({
                      ...prev,
                      isRecurring: e.target.checked
                    }))}
                  />
                  <span>Make this a recurring event</span>
                </label>
              </div>
              
              {newEvent.isRecurring && (
                <div className="recurring-options">
                  <p className="section-hint">
                    Create multiple events based on a recurrence pattern. All occurrences will be created immediately.
                  </p>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="recurrence-frequency">Frequency</label>
                      <select
                        id="recurrence-frequency"
                        value={newEvent.recurrencePattern.frequency}
                        onChange={(e) => setNewEvent(prev => ({
                          ...prev,
                          recurrencePattern: {
                            ...prev.recurrencePattern,
                            frequency: e.target.value,
                            daysOfWeek: [],
                            daysOfMonth: []
                          }
                        }))}
                      >
                        <option value="WEEKLY">Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="recurrence-interval">Interval</label>
                      <input
                        type="number"
                        id="recurrence-interval"
                        min="1"
                        max="12"
                        value={newEvent.recurrencePattern.interval}
                        onChange={(e) => setNewEvent(prev => ({
                          ...prev,
                          recurrencePattern: {
                            ...prev.recurrencePattern,
                            interval: parseInt(e.target.value) || 1
                          }
                        }))}
                        placeholder="1"
                      />
                      <small className="form-hint">
                        Every {newEvent.recurrencePattern.interval} {newEvent.recurrencePattern.frequency === 'WEEKLY' ? 'week(s)' : 'month(s)'}
                      </small>
                    </div>
                  </div>
                  
                  {newEvent.recurrencePattern.frequency === 'WEEKLY' && (
                    <div className="form-group">
                      <label>Days of Week</label>
                      <div className="days-selector">
                        {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map(day => (
                          <label key={day} className="day-checkbox">
                            <input
                              type="checkbox"
                              checked={newEvent.recurrencePattern.daysOfWeek.includes(day)}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setNewEvent(prev => ({
                                  ...prev,
                                  recurrencePattern: {
                                    ...prev.recurrencePattern,
                                    daysOfWeek: isChecked
                                      ? [...prev.recurrencePattern.daysOfWeek, day]
                                      : prev.recurrencePattern.daysOfWeek.filter(d => d !== day)
                                  }
                                }));
                              }}
                            />
                            <span>{day.substring(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                      <small className="form-hint">
                        Leave empty to repeat on the same day of the week as the start date
                      </small>
                    </div>
                  )}
                  
                  {newEvent.recurrencePattern.frequency === 'MONTHLY' && (
                    <div className="form-group">
                      <label htmlFor="days-of-month">Days of Month (comma-separated)</label>
                      <input
                        type="text"
                        id="days-of-month"
                        value={newEvent.recurrencePattern.daysOfMonth.join(', ')}
                        onChange={(e) => {
                          const days = e.target.value
                            .split(',')
                            .map(d => parseInt(d.trim()))
                            .filter(d => !isNaN(d) && d >= 1 && d <= 31);
                          setNewEvent(prev => ({
                            ...prev,
                            recurrencePattern: {
                              ...prev.recurrencePattern,
                              daysOfMonth: days
                            }
                          }));
                        }}
                        placeholder="e.g., 1, 15, 30"
                      />
                      <small className="form-hint">
                        Leave empty to repeat on the same day of the month as the start date
                      </small>
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="max-occurrences">Maximum Occurrences</label>
                      <input
                        type="number"
                        id="max-occurrences"
                        min="1"
                        max="100"
                        value={newEvent.recurrencePattern.maxOccurrences}
                        onChange={(e) => setNewEvent(prev => ({
                          ...prev,
                          recurrencePattern: {
                            ...prev.recurrencePattern,
                            maxOccurrences: parseInt(e.target.value) || 10
                          }
                        }))}
                        placeholder="10"
                      />
                      <small className="form-hint">
                        Total number of events to create (including the first)
                      </small>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="recurrence-end-date">Or End By Date</label>
                      <input
                        type="date"
                        id="recurrence-end-date"
                        value={newEvent.recurrencePattern.endDate || ''}
                        onChange={(e) => setNewEvent(prev => ({
                          ...prev,
                          recurrencePattern: {
                            ...prev.recurrencePattern,
                            endDate: e.target.value || null
                          }
                        }))}
                      />
                      <small className="form-hint">
                        Alternative to max occurrences
                      </small>
                    </div>
                  </div>
                  
                  <div className="recurring-summary">
                    <strong>📅 Summary:</strong> This will create up to {newEvent.recurrencePattern.maxOccurrences} events, 
                    repeating every {newEvent.recurrencePattern.interval} {newEvent.recurrencePattern.frequency === 'WEEKLY' ? 'week(s)' : 'month(s)'}
                    {newEvent.recurrencePattern.daysOfWeek.length > 0 && ` on ${newEvent.recurrencePattern.daysOfWeek.join(', ')}`}
                    {newEvent.recurrencePattern.daysOfMonth.length > 0 && ` on day(s) ${newEvent.recurrencePattern.daysOfMonth.join(', ')} of the month`}.
                  </div>
                  
                  {/* Calendar Preview */}
                  {shouldShowPreview && previewDates.length > 0 && (
                    <div className="calendar-preview-section">
                      <h4>📅 Recurring Events Preview</h4>
                      <p className="preview-hint">
                        {previewDates.length} event{previewDates.length !== 1 ? 's' : ''} will be created
                      </p>
                      
                      <div className="calendar-container">
                        {/* Calendar Grid */}
                        <div className="calendar-grid-wrapper">
                          <div className="calendar-navigation">
                            <button 
                              type="button" 
                              className="calendar-nav-btn"
                              onClick={handlePrevMonth}
                              aria-label="Previous month"
                            >
                              ←
                            </button>
                            <span className="calendar-month-name">
                              {getMonthName(previewCalendarMonth, previewCalendarYear)}
                            </span>
                            <button 
                              type="button" 
                              className="calendar-nav-btn"
                              onClick={handleNextMonth}
                              aria-label="Next month"
                            >
                              →
                            </button>
                          </div>
                          
                          <div className="calendar-grid">
                            {/* Day headers */}
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                              <div key={day} className="calendar-day-header">
                                {day}
                              </div>
                            ))}
                            
                            {/* Calendar cells */}
                            {getCalendarGrid.map((date, index) => (
                              <div
                                key={index}
                                className={`calendar-cell ${
                                  !date ? 'empty' : ''
                                } ${
                                  date && isToday(date) ? 'today' : ''
                                } ${
                                  date && isDateInPreview(date) ? 'has-event' : ''
                                }`}
                              >
                                {date ? date.getDate() : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Date List */}
                        <div className="calendar-dates-list">
                          <h5>Scheduled Occurrences:</h5>
                          <div className="dates-scroll-container">
                            {previewDates.slice(0, 20).map((date, index) => (
                              <div key={index} className="date-list-item">
                                <span className="date-number">{index + 1}.</span>
                                <span className="date-text">
                                  {date.toLocaleString('en-US', { 
                                    weekday: 'short', 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            ))}
                            {previewDates.length > 20 && (
                              <div className="date-list-item more-dates">
                                <span className="date-text">
                                  ...and {previewDates.length - 20} more occurrence{previewDates.length - 20 !== 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {shouldShowPreview && previewDates.length === 0 && (
                    <div className="calendar-preview-section">
                      <p className="preview-empty">
                        ℹ️ No matching dates found. Please adjust your recurrence pattern.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={actionLoading}>
                {actionLoading ? 'Creating...' : <><FontAwesomeIcon icon={faPlus} /> {newEvent.isRecurring ? 'Create Recurring Events' : 'Add Event'}</>}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Edit event form */}
      {showEditForm && currentEvent && (
        <div className="add-event-form-container">
          <button
            type="button"
            className="form-back-link"
            onClick={() => setShowEditForm(false)}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Events
          </button>
          <h3>Edit Event</h3>

          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleUpdateEvent} className="add-event-form">
            <div className="form-group">
              <label htmlFor="edit-name">Event Name</label>
              <input
                type="text"
                id="edit-name"
                name="name"
                value={editEvent.name}
                onChange={(e) => handleInputChange(e, 'edit')}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="edit-description">Description</label>
              <textarea
                id="edit-description"
                name="description"
                rows="3"
                value={editEvent.description}
                onChange={(e) => handleInputChange(e, 'edit')}
              />
            </div>
            
            {/* Hide venue section for virtual events */}
            {!editEvent.isVirtualEvent && (
            <div className="form-section venue-section">
              <h4>📍 Event Location</h4>
              <p className="section-hint">Select an existing venue or search for a new one</p>
              
              {/* Toggle between existing venue and new venue */}
              <div className="venue-toggle-buttons" style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className={`venue-toggle-btn ${useExistingVenueEdit ? 'active' : ''}`}
                  onClick={() => {
                    setUseExistingVenueEdit(true);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: useExistingVenueEdit ? '2px solid #5c6bc0' : '1px solid #ddd',
                    backgroundColor: useExistingVenueEdit ? '#e8eaf6' : '#fff',
                    color: useExistingVenueEdit ? '#5c6bc0' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: useExistingVenueEdit ? '600' : '400',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📋 Select Existing Venue
                </button>
                <button
                  type="button"
                  className={`venue-toggle-btn ${!useExistingVenueEdit ? 'active' : ''}`}
                  onClick={() => {
                    setUseExistingVenueEdit(false);
                    // Clear venue data when switching to search mode
                    setEditEvent(prev => ({ ...prev, venue: null, venueId: null }));
                    setCustomVenueNameEdit('');
                  }}
                  style={{
                    padding: '10px 20px',
                    border: !useExistingVenueEdit ? '2px solid #5c6bc0' : '1px solid #ddd',
                    backgroundColor: !useExistingVenueEdit ? '#e8eaf6' : '#fff',
                    color: !useExistingVenueEdit ? '#5c6bc0' : '#666',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: !useExistingVenueEdit ? '600' : '400',
                    transition: 'all 0.3s ease'
                  }}
                >
                  🔍 Search New Venue
                </button>
                {/* DV-260: Browse & Select aligned on the same row as the "Select Existing Venue" toggle */}
                {useExistingVenueEdit && !loadingVenues && venues.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleOpenVenueModal('edit')}
                    style={{
                      padding: '10px 20px',
                      border: '2px dashed #5c6bc0',
                      borderRadius: '6px',
                      backgroundColor: editEvent.venue ? '#e8eaf6' : '#f5f7fa',
                      color: '#5c6bc0',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {editEvent.venue ? (
                      <span>✓ {editEvent.venue.name} — Click to change</span>
                    ) : (
                      <span>📍 Browse &amp; Select Venue ({venues.length})</span>
                    )}
                  </button>
                )}
              </div>

              {/* Existing-venue loading / empty states */}
              {useExistingVenueEdit && loadingVenues && (
                <div style={{ padding: '12px', color: '#666', fontStyle: 'italic' }}>
                  Loading venues...
                </div>
              )}
              {useExistingVenueEdit && !loadingVenues && venues.length === 0 && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  color: '#856404'
                }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    ℹ️ No venues available. Click "Search New Venue" to create one.
                  </p>
                </div>
              )}
              
              {/* New Venue Search */}
              {!useExistingVenueEdit && (
                <div className="form-group">
                  <label htmlFor="edit-venue">Search for Venue *</label>
                  <GoogleMapsLocationPicker
                    key="edit-venue-picker" // Force remount when switching modes
                    onLocationSelect={handleEditLocationSelect}
                    initialLocation={editEvent.venue ? {
                      latitude: editEvent.venue.latitude,
                      longitude: editEvent.venue.longitude
                    } : null}
                    placeholder="Search for venue or click on the map..."
                  />
                </div>
              )}
              
              {/* Venue Info Display */}
              {editEvent.venue && (
                <>
                  {/* Editable Venue Name */}
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label htmlFor="custom-venue-name-edit">
                      Venue Name *
                    </label>
                    <input
                      type="text"
                      id="custom-venue-name-edit"
                      value={customVenueNameEdit}
                      onChange={(e) => setCustomVenueNameEdit(e.target.value)}
                      placeholder="Enter venue name"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '14px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        color: '#333',
                        fontFamily: 'inherit',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    <small className="form-hint" style={{ 
                      display: 'block', 
                      marginTop: '6px', 
                      fontSize: '12px', 
                      color: '#718096',
                      fontStyle: 'italic'
                    }}>
                      You can customize the venue name or keep the name from Google Maps
                    </small>
                  </div>
                  
                  <div className="venue-info-box">
                    <div className="venue-info-header">
                      <span className="venue-icon">📍</span>
                      <strong>{customVenueNameEdit || editEvent.venue.name}</strong>
                      {editEvent.venue.rating && (
                        <span className="venue-rating" style={{ marginLeft: '12px', color: '#f57c00', fontSize: '14px' }}>
                          ⭐ {editEvent.venue.rating} {editEvent.venue.userRatingsTotal && `(${editEvent.venue.userRatingsTotal} reviews)`}
                        </span>
                      )}
                    </div>
                    <div className="venue-info-details">
                      <p className="venue-address">
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {editEvent.venue.formattedAddress}
                      </p>
                      {editEvent.venue.city && editEvent.venue.state && (
                        <p className="venue-location-info">
                          {editEvent.venue.city}, {editEvent.venue.state} {editEvent.venue.postalCode}
                        </p>
                      )}
                      {editEvent.venue.timezone && (
                        <p className="venue-timezone">
                          🌍 Timezone: <strong>{editEvent.venue.timezone}</strong>
                        </p>
                      )}
                      {editEvent.venue.phoneNumber && (
                        <p className="venue-phone" style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>
                          📞 {editEvent.venue.phoneNumber}
                        </p>
                      )}
                      {editEvent.venue.website && (
                        <p className="venue-website" style={{ fontSize: '13px', marginTop: '6px' }}>
                          🔗 <a href={editEvent.venue.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                            Visit Website
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Save Venue Option - Only show if searching new venue */}
                  {!useExistingVenueEdit && (
                    <div className="save-venue-option" style={{ 
                      marginTop: '12px', 
                      padding: '12px', 
                      backgroundColor: '#e8f5e9',
                      border: '1px solid #4caf50',
                      borderRadius: '6px'
                    }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#2e7d32'
                      }}>
                        <input
                          type="checkbox"
                          checked={saveNewVenueEdit}
                          onChange={(e) => setSaveNewVenueEdit(e.target.checked)}
                          style={{ 
                            marginRight: '10px',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        💾 Save this venue to the database for future events
                      </label>
                      <p style={{ 
                        margin: '8px 0 0 28px', 
                        fontSize: '12px', 
                        color: '#558b2f',
                        fontStyle: 'italic'
                      }}>
                        ✓ Recommended: This allows you to reuse this venue for other events
                      </p>
                    </div>
                  )}
                </>
              )}
              
              {/* Manual location input as fallback */}
              {!editEvent.venue && (
                <div className="form-group">
                  <label htmlFor="edit-location-manual">Or enter location manually</label>
                  <input
                    type="text"
                    id="edit-location-manual"
                    name="location"
                    value={editEvent.location}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    placeholder="Enter location manually if venue not found"
                  />
                </div>
              )}
            </div>
            )}
            
            {/* Show Virtual location indicator for virtual events */}
            {editEvent.isVirtualEvent && (
              <div className="form-group">
                <label>Event Location</label>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  border: '2px solid #2196f3',
                  borderRadius: '6px',
                  color: '#1565c0',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🌐 Virtual Event - No physical venue required
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="edit-timezone">Event Timezone *</label>
              <p className="field-hint">
                {editEvent.venue 
                  ? '✅ Auto-populated from venue location' 
                  : 'Select timezone manually or search for a venue above'}
              </p>
              <TimezoneSelector
                selectedTimezone={editEvent.timezone}
                onTimezoneChange={(tz) => setEditEvent(prev => ({ ...prev, timezone: tz }))}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-startDate">Start Date & Time ({editEvent.timezone || 'Select timezone'})</label>
                <input
                  type="datetime-local"
                  id="edit-startDate"
                  name="startDate"
                  value={editEvent.startDate}
                  onChange={(e) => handleInputChange(e, 'edit')}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-endDate">End Date & Time ({editEvent.timezone || 'Select timezone'})</label>
                {!editEvent.endDate && editEvent.startDate && (
                  <p className="field-hint" style={{ color: '#28a745', fontSize: '12px', marginBottom: '4px' }}>
                    ✨ Will auto-populate when you select start date
                  </p>
                )}
                <input
                  type="datetime-local"
                  id="edit-endDate"
                  name="endDate"
                  value={editEvent.endDate}
                  onChange={(e) => handleInputChange(e, 'edit')}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-eventType">Event Type</label>
                <select
                  id="edit-eventType"
                  name="eventType"
                  value={editEvent.eventType}
                  onChange={(e) => handleInputChange(e, 'edit')}
                  required
                >
                  <option value="CONFERENCE">Conference</option>
                  <option value="SEMINAR">Seminar</option>
                  <option value="OPEN">Open</option>
                  <option value="WORKSHOP">Workshop</option>
                  <option value="EXCLUSIVE">Exclusive</option>
                </select>
              </div>
            </div>
            
            {/* Pin Level Selection for EXCLUSIVE events */}
            {editEvent.eventType === 'EXCLUSIVE' && (
              <div className="form-group exclusive-pin-level-section">
                <label htmlFor="edit-minPinLevelRank">
                  <span className="required-star">*</span> Minimum Pin Level Required
                  <span className="field-hint"> (Only users with this pin level or higher can see this event)</span>
                </label>
                <select
                  id="edit-minPinLevelRank"
                  name="minPinLevelRank"
                  value={editEvent.minPinLevelRank || ''}
                  onChange={(e) => handleInputChange(e, 'edit')}
                  required
                  className="pin-level-select"
                >
                  <option value="">Select minimum pin level...</option>
                  <optgroup label="Percentage Pins">
                    <option value="1">3% and above</option>
                    <option value="2">6% and above</option>
                    <option value="3">9% and above</option>
                    <option value="4">12% and above</option>
                    <option value="5">15% and above</option>
                    <option value="6">18% and above</option>
                    <option value="7">21% and above</option>
                    <option value="8">25% and above</option>
                  </optgroup>
                  <optgroup label="Leadership Pins">
                    <option value="9">Silver Sponsor and above</option>
                    <option value="10">Silver and above</option>
                    <option value="11">Gold Sponsor and above</option>
                    <option value="12">Gold and above</option>
                    <option value="13">Platinum and above</option>
                    <option value="14">Ruby and above</option>
                    <option value="15">Sapphire and above</option>
                    <option value="16">Emerald and above</option>
                    <option value="17">Diamond and above</option>
                    <option value="18">Crown Ambassador only</option>
                  </optgroup>
                </select>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="edit-image">Event Image (optional)</label>
              <input
                type="file"
                id="edit-image"
                name="image"
                accept="image/*"
                onChange={(e) => handleInputChange(e, 'edit')}
                className="file-input"
              />
              {editEvent.image && (
                <div className="image-preview">
                  <img 
                    src={URL.createObjectURL(editEvent.image)} 
                    alt="Event preview" 
                    className="preview-image"
                  />
                  <p className="preview-filename">{editEvent.image.name}</p>
                </div>
              )}
              {currentEvent?.imageUrl && !editEvent.image && (
                <div className="current-image-preview">
                  <p>Current image:</p>
                  <img 
                    src={currentEvent.imageUrl} 
                    alt="Current event image" 
                    className="current-preview-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="edit-status">Status</label>
              <select
                id="edit-status"
                name="status"
                value={editEvent.status}
                onChange={(e) => handleInputChange(e, 'edit')}
                required
              >
                <option value="UPCOMING">Upcoming</option>
                <option value="ACTIVE">Active</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            
            <div className="form-group">
              <label><FontAwesomeIcon icon={faUsers} /> Eligible Teams</label>
              <div className="team-selection-container">
                {loadingTeams ? (
                  <div className="loading-teams">Loading teams...</div>
                ) : (
                  <>
                    <div className="team-checkbox-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          value="all"
                          checked={editEvent.eligibleTeams.includes('all')}
                          onChange={(e) => handleTeamSelection(e, 'edit')}
                        />
                        <span className="checkmark"></span>
                        All Teams
                      </label>
                    </div>
                    {teams.map(team => (
                      <div key={team.id} className="team-checkbox-item">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            value={team.name}
                            checked={editEvent.eligibleTeams.includes(team.name)}
                            onChange={(e) => handleTeamSelection(e, 'edit')}
                            disabled={editEvent.eligibleTeams.includes('all')}
                          />
                          <span className="checkmark"></span>
                          {team.name}
                        </label>
                      </div>
                    ))}
                    {teams.length === 0 && (
                      <div className="no-teams-message">No teams available</div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Event Schedule (Stagetimer) */}
            <div className="form-group">
              <label htmlFor="edit-stagetimerRoomId">Event Schedule (Optional)</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  id="edit-stagetimerRoomId"
                  name="stagetimerRoomId"
                  value={editEvent.stagetimerRoomId || ''}
                  onChange={(e) => handleInputChange(e, 'edit')}
                  style={{ flex: 1 }}
                >
                  <option value="">-- No Schedule --</option>
                  {stagetimerRooms.map(room => (
                    <option key={room.roomId} value={room.roomId}>{room.roomName} ({room.roomId})</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={() => { setShowAddRoomModal(true); setAddRoomError(''); setNewRoomId(''); }} style={{ whiteSpace: 'nowrap' }}>
                  + Add Room
                </button>
              </div>
              <span className="field-hint">Link a stagetimer.io room to show event schedule to attendees</span>
            </div>

            {/* Hotel Booking Link */}
            <div className="form-group">
              <label htmlFor="edit-hotelBookingUrl">Hotel Booking Link (Optional)</label>
              <input
                type="url"
                id="edit-hotelBookingUrl"
                name="hotelBookingUrl"
                value={editEvent.hotelBookingUrl || ''}
                onChange={(e) => handleInputChange(e, 'edit')}
                placeholder="https://hotel-booking-link.com"
              />
              <span className="field-hint">If set, ticket holders can book a hotel from their ticket and ticket details pages</span>
            </div>

            {/* Virtual Event Section */}
            <div className="form-group checkbox-group">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  name="isVirtualEvent"
                  checked={editEvent.isVirtualEvent || false}
                  onChange={(e) => {
                    setEditEvent({
                      ...editEvent,
                      isVirtualEvent: e.target.checked,
                      location: e.target.checked ? 'Virtual' : (currentEvent?.location || ''),
                      venue: e.target.checked ? null : editEvent.venue,
                      venueId: e.target.checked ? null : editEvent.venueId,
                      virtualEventDetails: e.target.checked ? {
                        streamingUrl: editEvent.virtualEventDetails?.streamingUrl || '',
                        chatUrl: editEvent.virtualEventDetails?.chatUrl || '',
                        accessWindowHours: editEvent.virtualEventDetails?.accessWindowHours || 4,
                        allowReplay: editEvent.virtualEventDetails?.allowReplay || false,
                        replayDurationHours: editEvent.virtualEventDetails?.replayDurationHours || 24
                      } : null
                    });
                    // Clear custom venue name when switching to virtual
                    if (e.target.checked) {
                      setCustomVenueNameEdit('');
                    }
                  }}
                />
                <span>This is a Virtual/Online Event</span>
              </label>
            </div>
            
            {/* Virtual Event Details - Conditional Section */}
            {editEvent.isVirtualEvent && (
              <div className="virtual-event-section">
                <h4>Virtual Event Configuration</h4>
                <p className="section-hint">Configure streaming and access settings for this virtual event</p>
                
                <div className="form-group">
                  <label htmlFor="edit-streaming-url">Streaming URL *</label>
                  <textarea
                    id="edit-streaming-url"
                    name="streamingUrl"
                    value={editEvent.virtualEventDetails?.streamingUrl || ''}
                    onChange={(e) => {
                      setEditEvent({
                        ...editEvent,
                        virtualEventDetails: {
                          ...editEvent.virtualEventDetails,
                          streamingUrl: e.target.value
                        }
                      });
                    }}
                    placeholder="Enter embedded video URL (e.g., Vimeo, YouTube embed URL)"
                    rows="3"
                    required={editEvent.isVirtualEvent}
                  />
                  <p className="field-hint">Example: https://player.vimeo.com/video/123456789</p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="edit-chat-url">Chat URL (Optional)</label>
                  <textarea
                    id="edit-chat-url"
                    name="chatUrl"
                    value={editEvent.virtualEventDetails?.chatUrl || ''}
                    onChange={(e) => {
                      setEditEvent({
                        ...editEvent,
                        virtualEventDetails: {
                          ...editEvent.virtualEventDetails,
                          chatUrl: e.target.value
                        }
                      });
                    }}
                    placeholder="Enter chat/discussion URL (optional)"
                    rows="2"
                  />
                  <p className="field-hint">Optional: Link to live chat, Q&A, or discussion platform</p>
                </div>
                
                <div className="form-group">
                  <label htmlFor="edit-access-window">Access Window (Hours Before Event) *</label>
                  <input
                    type="number"
                    id="edit-access-window"
                    name="accessWindowHours"
                    value={editEvent.virtualEventDetails?.accessWindowHours || 4}
                    onChange={(e) => {
                      setEditEvent({
                        ...editEvent,
                        virtualEventDetails: {
                          ...editEvent.virtualEventDetails,
                          accessWindowHours: parseInt(e.target.value)
                        }
                      });
                    }}
                    min="1"
                    max="24"
                    required={editEvent.isVirtualEvent}
                  />
                  <p className="field-hint">Users can access the event this many hours before it starts (1-24 hours)</p>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      name="allowReplay"
                      checked={editEvent.virtualEventDetails?.allowReplay || false}
                      onChange={(e) => {
                        setEditEvent({
                          ...editEvent,
                          virtualEventDetails: {
                            ...editEvent.virtualEventDetails,
                            allowReplay: e.target.checked
                          }
                        });
                      }}
                    />
                    <span>Allow Replay After Event</span>
                  </label>
                  <p className="field-hint">Allow users to watch the recording after the event ends</p>
                </div>
                
                {editEvent.virtualEventDetails?.allowReplay && (
                  <div className="form-group">
                    <label htmlFor="edit-replay-duration">Replay Duration (Hours) *</label>
                    <input
                      type="number"
                      id="edit-replay-duration"
                      name="replayDurationHours"
                      value={editEvent.virtualEventDetails?.replayDurationHours || 24}
                      onChange={(e) => {
                        setEditEvent({
                          ...editEvent,
                          virtualEventDetails: {
                            ...editEvent.virtualEventDetails,
                            replayDurationHours: parseInt(e.target.value)
                          }
                        });
                      }}
                      min="1"
                      max="720"
                      required={editEvent.virtualEventDetails?.allowReplay}
                    />
                    <p className="field-hint">How long the replay will be available after the event (1-720 hours / 30 days)</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="form-actions">
              <button type="button" onClick={() => setShowEditForm(false)} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={actionLoading}>
                {actionLoading ? 'Updating...' : 'Update Event'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Delete confirmation */}
      {showDeleteConfirm && currentEvent && (
        <div className="delete-confirmation">
          <div className="delete-confirm-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete the event: <strong>{currentEvent.name}</strong>?</p>
            <p className="warning">This action cannot be undone!</p>
            
            <div className="confirm-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)} 
                className="cancel-delete-button"
                disabled={actionLoading}
              >
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
              <button 
                onClick={handleDeleteEvent} 
                className="confirm-delete-button"
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : <><FontAwesomeIcon icon={faCheck} /> Confirm Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Ticket Management Modal */}
      {showTicketManagement && currentEventForTickets && (
        <div className="ticket-management-modal">
          <div className="ticket-management-content">
            <div className="ticket-management-header">
              <h3>Manage Tickets - {currentEventForTickets.name}</h3>
              <button 
                className="close-modal-button"
                onClick={() => setShowTicketManagement(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="ticket-management-actions">
              <button 
                className="add-ticket-button"
                onClick={handleAddTicket}
              >
                <FontAwesomeIcon icon={faPlus} /> Add New Ticket Type
              </button>
            </div>

            {/* Ticket Form */}
            {showTicketForm && (
              <div className="ticket-form-container">
                <h4>{editingTicket ? 'Edit Ticket Type' : 'Add New Ticket Type'}</h4>
                
                {/* Timezone Information Banner */}
                <div className="timezone-info-banner" style={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '20px' }}>🌍</span>
                  <div>
                    <strong style={{ color: '#1976d2', fontSize: '14px' }}>Event Timezone</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#555' }}>
                      All dates and times for this ticket are in <strong>{currentEventForTickets?.timezone || 'America/Los_Angeles'}</strong> timezone.
                      <br />
                      Event: <strong>{currentEventForTickets?.name}</strong> | 
                      Ends: <strong>{currentEventForTickets?.endDate ? formatDate(currentEventForTickets.endDate, currentEventForTickets?.timezone) : 'N/A'}</strong>
                    </p>
                  </div>
                </div>
                
                <form onSubmit={handleTicketSubmit} className="ticket-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="ticket-name">Ticket Name *</label>
                      <input
                        type="text"
                        id="ticket-name"
                        name="name"
                        value={ticketFormData.name}
                        onChange={handleTicketFormChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="ticket-type">Ticket Type</label>
                      <select
                        id="ticket-type"
                        name="ticketType"
                        value={ticketFormData.ticketType}
                        onChange={handleTicketFormChange}
                      >
                        <option value="REGULAR">Regular</option>
                        <option value="VIP">VIP</option>
                        <option value="PREMIUM">Premium</option>
                        <option value="EARLY_BIRD">Early Bird</option>
                        <option value="GROUP">Group</option>
                        <option value="COMPLIMENTARY">Complimentary</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="ticket-description">Description</label>
                    <textarea
                      id="ticket-description"
                      name="description"
                      value={ticketFormData.description}
                      onChange={handleTicketFormChange}
                      rows="2"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="ticket-price">Price *</label>
                      <input
                        type="number"
                        step="0.01"
                        id="ticket-price"
                        name="price"
                        value={ticketFormData.price}
                        onChange={handleTicketFormChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="ticket-currency">Currency</label>
                      <select
                        id="ticket-currency"
                        name="currency"
                        value={ticketFormData.currency}
                        onChange={handleTicketFormChange}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="ticket-tax">Tax Percentage</label>
                      <input
                        type="number"
                        step="0.01"
                        id="ticket-tax"
                        name="taxPercentage"
                        value={ticketFormData.taxPercentage}
                        onChange={handleTicketFormChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="ticket-service">Service Charge</label>
                      <input
                        type="number"
                        step="0.01"
                        id="ticket-service"
                        name="serviceCharge"
                        value={ticketFormData.serviceCharge}
                        onChange={handleTicketFormChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="ticket-order">Display Order</label>
                      <input
                        type="number"
                        id="ticket-order"
                        name="displayOrder"
                        value={ticketFormData.displayOrder}
                        onChange={handleTicketFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-section" style={{ 
                    backgroundColor: '#f5f9fc', 
                    padding: '16px', 
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    marginBottom: '16px'
                  }}>
                    <h5 style={{ 
                      marginTop: 0, 
                      marginBottom: '12px', 
                      color: '#1976d2',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      📅 Sale Period
                      <span style={{ 
                        fontSize: '12px', 
                        fontWeight: 'normal', 
                        color: '#666',
                        backgroundColor: '#fff',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}>
                        Timezone: {currentEventForTickets?.timezone || 'America/Los_Angeles'}
                      </span>
                    </h5>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="sale-start" style={{ fontWeight: '600' }}>
                          Sale Start Date & Time
                        </label>
                        <p className="field-hint" style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginBottom: '6px',
                          fontStyle: 'italic'
                        }}>
                          When ticket sales begin (default: now)
                        </p>
                        <input
                          type="datetime-local"
                          id="sale-start"
                          name="saleStartDate"
                          value={ticketFormData.saleStartDate}
                          onChange={handleTicketFormChange}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="sale-end" style={{ fontWeight: '600' }}>
                          Sale End Date & Time
                        </label>
                        <p className="field-hint" style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginBottom: '6px',
                          fontStyle: 'italic'
                        }}>
                          When ticket sales close (default: event end)
                        </p>
                        <input
                          type="datetime-local"
                          id="sale-end"
                          name="saleEndDate"
                          value={ticketFormData.saleEndDate}
                          onChange={handleTicketFormChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-section" style={{ 
                    backgroundColor: '#fff8e1', 
                    padding: '16px', 
                    borderRadius: '8px',
                    border: '1px solid #ffc107',
                    marginBottom: '16px'
                  }}>
                    <h5 style={{ 
                      marginTop: 0, 
                      marginBottom: '12px', 
                      color: '#f57c00',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      ⚡ Early Bird Pricing (Optional)
                      <span style={{ 
                        fontSize: '12px', 
                        fontWeight: 'normal', 
                        color: '#666',
                        backgroundColor: '#fff',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}>
                        Timezone: {currentEventForTickets?.timezone || 'America/Los_Angeles'}
                      </span>
                    </h5>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="early-bird-price" style={{ fontWeight: '600' }}>
                          Early Bird Price
                        </label>
                        <p className="field-hint" style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginBottom: '6px',
                          fontStyle: 'italic'
                        }}>
                          Discounted price before deadline
                        </p>
                        <input
                          type="number"
                          step="0.01"
                          id="early-bird-price"
                          name="earlyBirdPrice"
                          value={ticketFormData.earlyBirdPrice}
                          onChange={handleTicketFormChange}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="early-bird-end" style={{ fontWeight: '600' }}>
                          Early Bird End Date & Time
                        </label>
                        <p className="field-hint" style={{ 
                          fontSize: '12px', 
                          color: '#666', 
                          marginBottom: '6px',
                          fontStyle: 'italic'
                        }}>
                          When early bird pricing expires
                        </p>
                        <input
                          type="datetime-local"
                          id="early-bird-end"
                          name="earlyBirdEndDate"
                          value={ticketFormData.earlyBirdEndDate}
                          onChange={handleTicketFormChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="group-price">Group Discount Price</label>
                      <input
                        type="number"
                        step="0.01"
                        id="group-price"
                        name="groupDiscountPrice"
                        value={ticketFormData.groupDiscountPrice}
                        onChange={handleTicketFormChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="min-group">Minimum Group Size</label>
                      <input
                        type="number"
                        id="min-group"
                        name="minimumGroupSize"
                        value={ticketFormData.minimumGroupSize}
                        onChange={handleTicketFormChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="active"
                        checked={ticketFormData.active}
                        onChange={handleTicketFormChange}
                      />
                      Active (available for sale)
                    </label>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      onClick={() => setShowTicketForm(false)} 
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="submit-button" 
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Saving...' : (editingTicket ? 'Update Ticket' : 'Create Ticket')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Ticket List */}
            <div className="ticket-list-container">
              <h4>Current Ticket Types</h4>
              {loadingTickets ? (
                <div className="loading-indicator">Loading tickets...</div>
              ) : ticketPrices.length > 0 ? (
                <div className="ticket-list">
                  {ticketPrices
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                    .map(ticket => (
                    <div key={ticket.id} className={`ticket-item ${ticket.active ? 'active' : 'inactive'}`}>
                      <div className="ticket-info">
                        <div className="ticket-header">
                          <h5>{ticket.name}</h5>
                          <div className="ticket-badges">
                            <span className={`ticket-type-badge ${ticket.ticketType.toLowerCase()}`}>
                              {ticket.ticketType}
                            </span>
                            {ticket.active ? (
                              <span className="status-badge active">Active</span>
                            ) : (
                              <span className="status-badge inactive">Inactive</span>
                            )}
                            {isEarlyBirdActive(ticket) && (
                              <span className="early-bird-badge">Early Bird</span>
                            )}
                          </div>
                        </div>
                        
                        <p className="ticket-description">{ticket.description}</p>
                        
                        <div className="ticket-details">
                          <div className="price-info">
                            <span className="main-price">{formatPrice(ticket.price, ticket.currency)}</span>
                            {ticket.earlyBirdPrice && (
                              <span className="early-bird-price">
                                Early Bird: {formatPrice(ticket.earlyBirdPrice, ticket.currency)}
                              </span>
                            )}
                            {ticket.groupDiscountPrice && (
                              <span className="group-price">
                                Group ({ticket.minimumGroupSize}+): {formatPrice(ticket.groupDiscountPrice, ticket.currency)}
                              </span>
                            )}
                          </div>
                          
                          <div className="ticket-meta">
                            <span>Available: {ticket.availableQuantity}</span>
                            <span>Order: {ticket.displayOrder}</span>
                            {ticket.taxPercentage > 0 && <span>Tax: {ticket.taxPercentage}%</span>}
                            {ticket.serviceCharge > 0 && <span>Service: {formatPrice(ticket.serviceCharge, ticket.currency)}</span>}
                          </div>
                        </div>
                      </div>
                      
                      <div className="ticket-actions">
                        <button 
                          className="edit-ticket-button"
                          onClick={() => handleEditTicket(ticket)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                          className={`toggle-ticket-button ${ticket.active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleTicketActive(ticket.id, ticket.active)}
                        >
                          {ticket.active ? <FontAwesomeIcon icon={faTimes} /> : <FontAwesomeIcon icon={faCheck} />}
                        </button>
                        <button 
                          className="delete-ticket-button"
                          onClick={() => handleDeleteTicket(ticket.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-tickets">
                  <p>No ticket types found for this event.</p>
                  <p>Click "Add New Ticket Type" to create one.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* RSVP Management Modal */}
      {showRSVPManagement && currentEventForRSVP && (
        <div className="rsvp-management-modal">
          <div className="rsvp-management-content">
            <div className="rsvp-management-header">
              <h3>RSVPs - {currentEventForRSVP.name}</h3>
              <button 
                className="close-modal-button"
                onClick={() => setShowRSVPManagement(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Tab Navigation */}
            <div className="rsvp-tabs">
              <button 
                className={`rsvp-tab ${rsvpActiveTab === 'summary' ? 'active' : ''}`}
                onClick={() => setRSVPActiveTab('summary')}
              >
                <FontAwesomeIcon icon={faChartBar} />
                Summary
              </button>
              <button 
                className={`rsvp-tab ${rsvpActiveTab === 'details' ? 'active' : ''}`}
                onClick={() => setRSVPActiveTab('details')}
              >
                <FontAwesomeIcon icon={faUsers} />
                Details
              </button>
            </div>

            {/* Tab Content */}
            <div className="rsvp-tab-content">
              {rsvpActiveTab === 'summary' && (
                <div className="rsvp-summary-section">
                  <h4>RSVP Summary</h4>
                  {loadingRSVPs ? (
                    <div className="loading-indicator">Loading RSVP summary...</div>
                  ) : rsvpSummary ? (
                    <div className="rsvp-summary-grid">
                      <div className="rsvp-summary-card going">
                        <div className="rsvp-summary-icon">
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                        <div className="rsvp-summary-info">
                          <h5>Going</h5>
                          <span className="rsvp-count">{rsvpSummary.goingCount || 0}</span>
                        </div>
                      </div>
                      
                      <div className="rsvp-summary-card not-going">
                        <div className="rsvp-summary-icon">
                          <FontAwesomeIcon icon={faTimes} />
                        </div>
                        <div className="rsvp-summary-info">
                          <h5>Not Going</h5>
                          <span className="rsvp-count">{rsvpSummary.notGoingCount || 0}</span>
                        </div>
                      </div>
                      
                      <div className="rsvp-summary-card maybe">
                        <div className="rsvp-summary-icon">
                          <FontAwesomeIcon icon={faEye} />
                        </div>
                        <div className="rsvp-summary-info">
                          <h5>Maybe</h5>
                          <span className="rsvp-count">{rsvpSummary.maybeCount || 0}</span>
                        </div>
                      </div>
                      
                      <div className="rsvp-summary-card total">
                        <div className="rsvp-summary-icon">
                          <FontAwesomeIcon icon={faUserCheck} />
                        </div>
                        <div className="rsvp-summary-info">
                          <h5>Total RSVPs</h5>
                          <span className="rsvp-count">{rsvpSummary.totalRsvps || 0}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-rsvp-data">
                      <p>No RSVP summary available for this event.</p>
                    </div>
                  )}
                </div>
              )}

              {rsvpActiveTab === 'details' && (
                <div className="rsvp-details-section">
                  <h4>RSVP Details</h4>
                  {loadingRSVPs ? (
                    <div className="loading-indicator">Loading RSVP details...</div>
                  ) : rsvpData.length > 0 ? (
                    <div className="rsvp-list">
                      {rsvpData.map(rsvp => (
                        <div 
                          key={rsvp.id} 
                          className="rsvp-item clickable"
                          onClick={() => handleViewUserProfile(rsvp.userId)}
                          title="Click to view user profile"
                        >
                          <div className="rsvp-user-info">
                            <h6>User ID: {rsvp.userId}</h6>
                            <p className="rsvp-dates">
                              <span>RSVP Date: {formatRSVPDate(rsvp.createdAt)}</span>
                              {rsvp.updatedAt !== rsvp.createdAt && (
                                <span>Updated: {formatRSVPDate(rsvp.updatedAt)}</span>
                              )}
                            </p>
                          </div>
                          <div 
                            className="rsvp-status"
                            style={{ backgroundColor: getRSVPStatusColor(rsvp.rsvpStatus) }}
                          >
                            <FontAwesomeIcon icon={getRSVPStatusIcon(rsvp.rsvpStatus)} />
                            <span>{rsvp.rsvpStatus.replace('_', ' ')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-rsvp-data">
                      <p>No RSVPs found for this event.</p>
                      <p>Attendees will be able to RSVP once the event is published.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* User Profile Modal */}
      {showUserProfile && (
        <div className="user-profile-modal">
          <div className="user-profile-content">
            <div className="user-profile-header">
              <h3>User Profile</h3>
              <button 
                className="close-modal-button"
                onClick={closeUserProfile}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="user-profile-body">
              {loadingUserProfile ? (
                <div className="loading-indicator">Loading user profile...</div>
              ) : userProfile ? (
                <div className="user-profile-details">
                  <div className="profile-section">
                    <h4>Basic Information</h4>
                    <div className="profile-grid">
                      <div className="profile-item">
                        <label>User ID:</label>
                        <span>{userProfile.id || 'N/A'}</span>
                      </div>
                      <div className="profile-item">
                        <label>Name:</label>
                        <span>{userProfile.name || userProfile.iboName || 'N/A'}</span>
                      </div>
                      <div className="profile-item">
                        <label>Email:</label>
                        <span>{userProfile.email || 'N/A'}</span>
                      </div>
                      <div className="profile-item">
                        <label>Phone:</label>
                        <span>{userProfile.phone || 'N/A'}</span>
                      </div>
                      <div className="profile-item">
                        <label>IBO Number:</label>
                        <span>{userProfile.iboNumber || 'N/A'}</span>
                      </div>
                      <div className="profile-item">
                        <label>Team:</label>
                        <span>{userProfile.teamName || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {userProfile.address && (
                    <div className="profile-section">
                      <h4>Address Information</h4>
                      <div className="profile-grid">
                        <div className="profile-item">
                          <label>Street:</label>
                          <span>{userProfile.address.street || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                          <label>City:</label>
                          <span>{userProfile.address.city || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                          <label>State:</label>
                          <span>{userProfile.address.state || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                          <label>ZIP Code:</label>
                          <span>{userProfile.address.zipCode || 'N/A'}</span>
                        </div>
                        <div className="profile-item">
                          <label>Country:</label>
                          <span>{userProfile.address.country || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="profile-section">
                    <h4>Account Status</h4>
                    <div className="profile-grid">
                      <div className="profile-item">
                        <label>Email Verified:</label>
                        <span className={`status-indicator ${userProfile.emailVerified ? 'verified' : 'unverified'}`}>
                          <FontAwesomeIcon icon={userProfile.emailVerified ? faCheck : faTimes} />
                          {userProfile.emailVerified ? 'Verified' : 'Not Verified'}
                        </span>
                      </div>
                      <div className="profile-item">
                        <label>User Verified:</label>
                        <span className={`status-indicator ${userProfile.userVerified ? 'verified' : 'unverified'}`}>
                          <FontAwesomeIcon icon={userProfile.userVerified ? faCheck : faTimes} />
                          {userProfile.userVerified ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {userProfile.firstContractYear && (
                        <div className="profile-item">
                          <label>First Contract Year:</label>
                          <span className="status-indicator first-year">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            {userProfile.firstContractYear}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(userProfile.createdAt || userProfile.updatedAt) && (
                    <div className="profile-section">
                      <h4>Account Information</h4>
                      <div className="profile-grid">
                        {userProfile.createdAt && (
                          <div className="profile-item">
                            <label>Joined:</label>
                            <span>{formatRSVPDate(userProfile.createdAt)}</span>
                          </div>
                        )}
                        {userProfile.updatedAt && (
                          <div className="profile-item">
                            <label>Last Updated:</label>
                            <span>{formatRSVPDate(userProfile.updatedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-profile-data">
                  <p>Unable to load user profile information.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Display the events list */}
      {!showAddForm && !showEditForm && renderEvents()}
        </>
      )}
      
      {/* Venues Tab Content */}
      {activeTab === 'venues' && (
        <VenueManagement />
      )}
      
      {/* Event Details Modal */}
      {showEventDetailsModal && selectedEventForDetails && (
        <div className="event-details-modal" onClick={() => setShowEventDetailsModal(false)}>
          <div className="event-details-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="event-details-modal-header">
              <h3>
                {selectedEventForDetails.name}
                {selectedEventForDetails.isVirtualEvent && (
                  <span className="virtual-event-badge" style={{ marginLeft: '8px' }}>
                    <FontAwesomeIcon icon={faVideo} /> Virtual
                  </span>
                )}
              </h3>
              <button 
                className="close-modal-button"
                onClick={() => setShowEventDetailsModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="event-details-modal-body">
              {/* Event Image */}
              {selectedEventForDetails.imageUrl && (
                <div className="event-details-image">
                  <img 
                    src={getOptimizedImageUrl(selectedEventForDetails.imageUrl)} 
                    alt={selectedEventForDetails.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Event Description */}
              {selectedEventForDetails.description && (
                <div className="event-details-section">
                  <h4>📝 Description</h4>
                  <p>{selectedEventForDetails.description}</p>
                </div>
              )}

              {/* Date & Time */}
              <div className="event-details-section">
                <h4>📅 Date & Time</h4>
                <p><strong>Start:</strong> {formatDate(selectedEventForDetails.startDate, selectedEventForDetails.timezone)}</p>
                {selectedEventForDetails.endDate && selectedEventForDetails.endDate !== selectedEventForDetails.startDate && (
                  <p><strong>End:</strong> {formatDate(selectedEventForDetails.endDate, selectedEventForDetails.timezone)}</p>
                )}
                <p><strong>Timezone:</strong> {selectedEventForDetails.timezone}</p>
              </div>

              {/* Venue Information */}
              <div className="event-details-section">
                <h4>📍 Location</h4>
                {selectedEventForDetails.venue ? (
                  <>
                    <p><strong>{selectedEventForDetails.venue.name}</strong></p>
                    {selectedEventForDetails.venue.rating && (
                      <p>⭐ {selectedEventForDetails.venue.rating} ({selectedEventForDetails.venue.userRatingsTotal} reviews)</p>
                    )}
                    <p>{selectedEventForDetails.venue.formattedAddress}</p>
                    {selectedEventForDetails.venue.phoneNumber && (
                      <p>📞 {selectedEventForDetails.venue.phoneNumber}</p>
                    )}
                    {selectedEventForDetails.venue.googleMapsUrl && (
                      <a 
                        href={selectedEventForDetails.venue.googleMapsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="maps-link"
                      >
                        🗺️ Open in Google Maps
                      </a>
                    )}
                  </>
                ) : (
                  <p>{selectedEventForDetails.location || 'No location specified'}</p>
                )}
              </div>

              {/* Event Info */}
              <div className="event-details-section">
                <h4>ℹ️ Event Information</h4>
                <p><strong>Type:</strong> {selectedEventForDetails.eventType}</p>
                <p><strong>Capacity:</strong> {selectedEventForDetails.capacity}</p>
                <p><strong>Status:</strong> <span className={`status-badge ${selectedEventForDetails.status.toLowerCase()}`}>{selectedEventForDetails.status}</span></p>
                {selectedEventForDetails.stagetimerRoomId && (
                  <p><strong>Stagetimer Room:</strong> {selectedEventForDetails.stagetimerRoomId}</p>
                )}
                {selectedEventForDetails.hotelBookingUrl && (
                  <p><strong>Hotel Booking Link:</strong>{' '}
                    <a href={selectedEventForDetails.hotelBookingUrl} target="_blank" rel="noopener noreferrer">
                      {selectedEventForDetails.hotelBookingUrl}
                    </a>
                  </p>
                )}
                {selectedEventForDetails.minPinLevelRank != null && selectedEventForDetails.minPinLevelRank > 0 && (
                  <p><strong>Min Pin Level Rank:</strong> {selectedEventForDetails.minPinLevelRank}</p>
                )}
              </div>

              {/* Virtual Event Details */}
              {selectedEventForDetails.isVirtualEvent && (
                <div className="event-details-section virtual-details-section">
                  <h4><FontAwesomeIcon icon={faVideo} /> Virtual Event Configuration</h4>
                  {modalVirtualDetailsLoading ? (
                    <p style={{ color: '#888', fontStyle: 'italic' }}>Loading virtual event details...</p>
                  ) : modalVirtualDetails ? (
                    <>
                      <div className="virtual-detail-row">
                        <strong>Status:</strong>
                        <span className={`virtual-detail-status ${modalVirtualDetails.isActive ? 'active' : 'inactive'}`}>
                          {modalVirtualDetails.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="virtual-detail-row">
                        <strong>Streaming URL:</strong>
                        <span className="virtual-detail-url">{modalVirtualDetails.streamingUrl || 'Not configured'}</span>
                      </div>
                      {modalVirtualDetails.chatUrl && (
                        <div className="virtual-detail-row">
                          <strong>Chat URL:</strong>
                          <span className="virtual-detail-url">{modalVirtualDetails.chatUrl}</span>
                        </div>
                      )}
                      <div className="virtual-detail-row">
                        <strong>Access Window:</strong>
                        <span>{modalVirtualDetails.accessWindowHours || 4} hours before event</span>
                      </div>
                      <div className="virtual-detail-row">
                        <strong>Replay:</strong>
                        <span>
                          {modalVirtualDetails.allowReplay
                            ? `Enabled (${modalVirtualDetails.replayDurationHours}h after event)`
                            : 'Disabled'}
                        </span>
                      </div>
                      {modalVirtualDetails.updatedAt && (
                        <div className="virtual-detail-row">
                          <strong>Last Updated:</strong>
                          <span>{new Date(modalVirtualDetails.updatedAt).toLocaleString()}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ color: '#888', fontStyle: 'italic' }}>No virtual event configuration found. Use "Virtual Config" to set it up.</p>
                  )}
                </div>
              )}

              {/* Eligible Teams */}
              <div className="event-details-section">
                <h4>👥 Eligible Teams</h4>
                <p>{getEligibleTeamsDisplay(selectedEventForDetails.eligibleTeams)}</p>
              </div>

              {/* Ticket Pricing */}
              {selectedEventForDetails.ticketPriceInfos && selectedEventForDetails.ticketPriceInfos.length > 0 && (
                <div className="event-details-section">
                  <h4>🎫 Ticket Pricing</h4>
                  <div className="ticket-types-list">
                    {selectedEventForDetails.ticketPriceInfos
                      .filter(ticket => ticket.active)
                      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                      .map(ticket => (
                        <div key={ticket.id} className="ticket-type-detail">
                          <div className="ticket-type-header">
                            <strong>{ticket.name}</strong>
                            <span className={`ticket-type-badge ${ticket.ticketType.toLowerCase()}`}>
                              {ticket.ticketType}
                            </span>
                          </div>
                          {ticket.description && <p className="ticket-description">{ticket.description}</p>}
                          <div className="ticket-pricing">
                            {isEarlyBirdActive(ticket) ? (
                              <>
                                <span className="early-bird-price-large">
                                  {formatPrice(ticket.earlyBirdPrice, ticket.currency)}
                                </span>
                                <span className="original-price-struck">
                                  {formatPrice(ticket.price, ticket.currency)}
                                </span>
                                <span className="early-bird-badge">Early Bird</span>
                              </>
                            ) : (
                              <span className="regular-price-large">
                                {formatPrice(ticket.price, ticket.currency)}
                              </span>
                            )}
                          </div>
                          <p className="ticket-availability">
                            {ticket.availableQuantity} tickets available
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="event-details-actions">
                <button 
                  className="action-btn manage-tickets-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEventDetailsModal(false);
                    handleManageTickets(selectedEventForDetails);
                  }}
                >
                  <FontAwesomeIcon icon={faTicketAlt} /> Manage Tickets
                </button>
                <button 
                  className="action-btn view-rsvp-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEventDetailsModal(false);
                    handleViewRSVPs(selectedEventForDetails);
                  }}
                >
                  <FontAwesomeIcon icon={faUserCheck} /> View RSVPs
                </button>
                {selectedEventForDetails.isVirtualEvent && (
                  <button 
                    className="action-btn virtual-config-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEventDetailsModal(false);
                      handleVirtualConfig(selectedEventForDetails);
                    }}
                  >
                    <FontAwesomeIcon icon={faVideo} /> Virtual Config
                  </button>
                )}
                <button 
                  className="action-btn post-event-content-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEventDetailsModal(false);
                    handlePostEventContent(selectedEventForDetails);
                  }}
                >
                  <FontAwesomeIcon icon={faGift} /> Post-Event Content
                </button>
                <button 
                  className="action-btn edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEventDetailsModal(false);
                    handleEditEvent(selectedEventForDetails);
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} /> Edit Event
                </button>
                <button 
                  className="action-btn delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEventDetailsModal(false);
                    handleDeletePrompt(selectedEventForDetails);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} /> Delete Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Virtual Config Modal */}
      {showVirtualConfigModal && virtualConfigEvent && (
        <div className="virtual-config-modal-overlay" onClick={() => setShowVirtualConfigModal(false)}>
          <div className="virtual-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="virtual-config-modal-header">
              <h3><FontAwesomeIcon icon={faVideo} /> Virtual Event Configuration</h3>
              <p className="virtual-config-event-name">{virtualConfigEvent.name}</p>
              <button onClick={() => setShowVirtualConfigModal(false)} className="close-modal-button">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {virtualConfigLoading ? (
              <div className="virtual-config-loading">
                <div className="spinner"></div>
                <p>Loading configuration...</p>
              </div>
            ) : (
              <>
                <div className="virtual-config-modal-body">
                  <div className="virtual-config-status-bar">
                    <span className={`virtual-status-badge ${virtualConfigForm.isActive ? 'active' : 'inactive'}`}>
                      {virtualConfigForm.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      className={`virtual-toggle-btn ${virtualConfigForm.isActive ? 'active' : 'inactive'}`}
                      onClick={() => setVirtualConfigForm({ ...virtualConfigForm, isActive: !virtualConfigForm.isActive })}
                    >
                      <FontAwesomeIcon icon={virtualConfigForm.isActive ? faToggleOn : faToggleOff} />
                      {virtualConfigForm.isActive ? 'Enabled' : 'Disabled'}
                    </button>
                    {!virtualConfigData && (
                      <span className="virtual-new-badge">New Configuration</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label><span className="required-star">*</span> Streaming URL</label>
                    <textarea
                      value={virtualConfigForm.streamingUrl}
                      onChange={(e) => setVirtualConfigForm({ ...virtualConfigForm, streamingUrl: e.target.value })}
                      placeholder="Enter embedded video URL (e.g., Vimeo, YouTube embed URL)"
                      rows="3"
                      required
                    />
                    <p className="field-hint">Example: https://player.vimeo.com/video/123456789</p>
                  </div>

                  <div className="form-group">
                    <label>Chat URL (optional)</label>
                    <textarea
                      value={virtualConfigForm.chatUrl}
                      onChange={(e) => setVirtualConfigForm({ ...virtualConfigForm, chatUrl: e.target.value })}
                      placeholder="Enter embedded chat URL"
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label>Access Window (hours before event)</label>
                    <input
                      type="number"
                      value={virtualConfigForm.accessWindowHours}
                      onChange={(e) => setVirtualConfigForm({ ...virtualConfigForm, accessWindowHours: parseInt(e.target.value) || 4 })}
                      min="1"
                      max="24"
                      required
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={virtualConfigForm.allowReplay}
                        onChange={(e) => setVirtualConfigForm({
                          ...virtualConfigForm,
                          allowReplay: e.target.checked,
                          replayDurationHours: e.target.checked ? (virtualConfigForm.replayDurationHours || 24) : null
                        })}
                      />
                      <span>Allow Replay After Event</span>
                    </label>
                  </div>

                  {virtualConfigForm.allowReplay && (
                    <div className="form-group">
                      <label>Replay Duration (hours after event ends)</label>
                      <input
                        type="number"
                        value={virtualConfigForm.replayDurationHours || ''}
                        onChange={(e) => setVirtualConfigForm({ ...virtualConfigForm, replayDurationHours: parseInt(e.target.value) || null })}
                        min="1"
                        placeholder="e.g., 24 for 1 day"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="virtual-config-modal-footer">
                  <button onClick={() => setShowVirtualConfigModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveVirtualConfig}
                    className="btn-primary"
                    disabled={virtualConfigSaving || !virtualConfigForm.streamingUrl}
                  >
                    {virtualConfigSaving ? 'Saving...' : (virtualConfigData ? 'Save Changes' : 'Create Configuration')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Venue Selection Modal */}
      {showVenueModal && (
        <div className="venue-selection-modal">
          <div className="venue-selection-modal-content">
            <div className="venue-selection-modal-header">
              <h3>📍 Select a Venue</h3>
              <button 
                className="close-modal-button"
                onClick={() => setShowVenueModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Search Bar */}
            <div className="venue-modal-search">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="search-icon" />
              <input
                type="text"
                placeholder="Search venues by name, city, or address..."
                value={venueSearchQuery}
                onChange={(e) => setVenueSearchQuery(e.target.value)}
                className="venue-modal-search-input"
              />
              {venueSearchQuery && (
                <button 
                  className="clear-search-btn" 
                  onClick={() => setVenueSearchQuery('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>

            {/* Venues Grid */}
            <div className="venue-modal-body">
              {loadingVenues ? (
                <div className="venue-modal-loading">Loading venues...</div>
              ) : venues.length === 0 ? (
                <div className="venue-modal-empty">
                  <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" />
                  <h4>No venues available</h4>
                  <p>Click "Search New Venue" to create a new venue using Google Maps</p>
                </div>
              ) : (
                <div className="venues-modal-grid">
                  {venues
                    .filter(venue => {
                      if (!venueSearchQuery) return true;
                      const query = venueSearchQuery.toLowerCase();
                      return (
                        venue.name.toLowerCase().includes(query) ||
                        venue.city?.toLowerCase().includes(query) ||
                        venue.state?.toLowerCase().includes(query) ||
                        venue.formattedAddress?.toLowerCase().includes(query)
                      );
                    })
                    .map(venue => (
                      <div 
                        key={venue.id} 
                        className="venue-modal-card"
                        onClick={() => handleSelectVenueFromModal(venue)}
                      >
                        {/* Google Maps Static Image */}
                        {venue.latitude && venue.longitude && (
                          <div className="venue-modal-card-map">
                            <img
                              src={`https://maps.googleapis.com/maps/api/staticmap?center=${venue.latitude},${venue.longitude}&zoom=15&size=400x200&markers=color:red%7C${venue.latitude},${venue.longitude}&key=${config.GOOGLE_MAPS_API_KEY}`}
                              alt={`Map of ${venue.name}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="venue-modal-card-map-placeholder" style={{ display: 'none' }}>
                              <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" />
                              <span style={{ fontSize: '12px', marginTop: '8px' }}>Map unavailable</span>
                            </div>
                          </div>
                        )}

                        <div className="venue-modal-card-content">
                          <div className="venue-modal-card-header">
                            <h4>{venue.name}</h4>
                            {venue.rating && (
                              <span className="venue-modal-card-rating">
                                ⭐ {venue.rating}
                                {venue.userRatingsTotal && (
                                  <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                                    ({venue.userRatingsTotal})
                                  </span>
                                )}
                              </span>
                            )}
                          </div>

                          <div className="venue-modal-card-details">
                            <p className="venue-modal-card-address">
                              <FontAwesomeIcon icon={faMapMarkerAlt} />
                              {venue.formattedAddress}
                            </p>

                            {venue.city && venue.state && (
                              <p className="venue-modal-card-location">
                                📍 {venue.city}, {venue.state} {venue.postalCode}
                              </p>
                            )}

                            <p className="venue-modal-card-timezone">
                              🌍 <strong>{venue.timezone}</strong>
                            </p>

                            {venue.phoneNumber && (
                              <p className="venue-modal-card-phone">
                                📞 {venue.phoneNumber}
                              </p>
                            )}

                            {venue.website && (
                              <p className="venue-modal-card-website">
                                🔗 <a 
                                  href={venue.website} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Visit Website
                                </a>
                              </p>
                            )}

                            {venue.googleMapsUrl && (
                              <p className="venue-modal-card-maps-link">
                                🗺️ <a 
                                  href={venue.googleMapsUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Open in Google Maps
                                </a>
                              </p>
                            )}
                          </div>

                          <button 
                            className="venue-modal-card-select-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectVenueFromModal(venue);
                            }}
                          >
                            <FontAwesomeIcon icon={faCheck} /> Select This Venue
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Stagetimer Room Registration Modal */}
      {showAddRoomModal && (
        <div className="modal-overlay" onClick={() => setShowAddRoomModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <h3>Register Stagetimer Room</h3>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px' }}>
              Enter the Room ID from your stagetimer.io controller page. It will be validated against the stagetimer API.
            </p>
            {addRoomError && <div className="error-message">{addRoomError}</div>}
            <div className="form-group">
              <label htmlFor="newRoomId">Room ID</label>
              <input
                type="text"
                id="newRoomId"
                value={newRoomId}
                onChange={e => setNewRoomId(e.target.value)}
                placeholder="e.g. 6ADP4P5U"
                disabled={addingRoom}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" className="btn-secondary" onClick={() => setShowAddRoomModal(false)} disabled={addingRoom}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleRegisterRoom} disabled={addingRoom || !newRoomId.trim()}>
                {addingRoom ? 'Validating...' : 'Register Room'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Event Content Panel */}
      {showPostEventContent && postEventContentEvent && (
        <PostEventContentPanel
          event={postEventContentEvent}
          onClose={() => {
            setShowPostEventContent(false);
            setPostEventContentEvent(null);
          }}
        />
      )}

      {/* Shared overflow (⋮) menu for event card secondary actions */}
      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={closeCardMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => runCardMenuAction(handleViewRSVPs)}>
          <ListItemIcon><FontAwesomeIcon icon={faUserCheck} /></ListItemIcon>
          <ListItemText>RSVPs</ListItemText>
        </MenuItem>
        {cardMenuEvent?.isVirtualEvent && (
          <MenuItem onClick={() => runCardMenuAction(handleVirtualConfig)}>
            <ListItemIcon><FontAwesomeIcon icon={faVideo} /></ListItemIcon>
            <ListItemText>Virtual Config</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => runCardMenuAction(handlePostEventContent)}>
          <ListItemIcon><FontAwesomeIcon icon={faGift} /></ListItemIcon>
          <ListItemText>Post-Event Content</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => runCardMenuAction(handleDeletePrompt)} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}><FontAwesomeIcon icon={faTrash} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default EventManagementPage; 